import Vue from 'vue';
import VueGtm from 'vue-gtm';
import Router from 'vue-router';
import store from '../store/index.js';
import VueMeta from 'vue-meta';
import Notify from 'quasar/src/plugins/Notify.js';;
import GotedoSpinnerLoading from '@/shared/spinners/GotedoSpinnerLoader.vue';
import i18next from 'i18next';
import { debounce } from 'lodash';
import { getAuthOrgId, isLoggedIn } from '../store/modules/auth.js';
import { appReady } from '../utils/app_readiness.js';

const churchId = () => store.getters['auth/inStoreResolvedOrgId']();
const impersonatedChurchId = () => store.getters['auth/GET_IMPERSONATION_SESSION'].churchId;
const isChurchOwner = () => !!store.getters['auth/isChurchOwner'];
const isGlobalUser = () => !!store.getters['auth/isGlobalUser'];
const inDevelopment = () => !!store.state.inDevelopment;
const isImpersonationActive = () =>
  !!store.getters['auth/GET_IMPERSONATION_SESSION']?.active && isGlobalUser();
const isAuthOrgVerified = () => store.getters['auth/isAuthOrgVerified'];
// const isOrgWithinVerificationGracePeriod = () =>
//   store.getters['auth/isOrgWithinVerificationGracePeriod'];

const validateTokenPayload = (payload) =>
  store.dispatch('membership_self_registrations/validateTokenPayload', payload);
const loadAuthProfile = async (churchId) =>
  store.dispatch('auth/loadAuthProfile', { churchId, isGlobalUser: isGlobalUser() });
const loadRoutePermissions = async (churchId) =>
  store.dispatch('permissions/loadRoutePermissions', { churchId });

const inStagingMode =
  process.env.VUE_APP_IN_STAGING_MODE === 'true' || process.env.VUE_APP_IN_STAGING_MODE === true;

const routeNoPermission = function (message) {
  Notify.create({
    message: message ?? i18next.t('router_file.access_to_page_denied', { ns: 'toasts' }),
    type: 'negative',
  });
};

Vue.use(VueMeta);

// Containers
const DefaultContainer = () => import('@/containers/AdminAreaLayout.vue');

// Views
const ChurchDashboard = () => ({
  component: import('@/views/dashboard/ChurchDashboard.vue'),
  loading: GotedoSpinnerLoading,
});
const Dashboard = () => ({
  // component: import('@/views/dashboard/Dashboard.vue'),
  component: import('@/views/dashboard/GlobalDashboard.vue'),
  loading: GotedoSpinnerLoading,
});

// Organisations
const Organisations = () => import('@/views/organisations/Organisations.vue');
const Organisation = () => import('@/views/organisations/Organisation.vue');
const OrganisationEditForm = () => import('@/views/settings/organisation/OrganisationEditForm.vue');
const OrganisationVerificationDocs = () =>
  import('@/views/settings/organisation/OrganisationVerificationDocs.vue');
const OrganisationPreferences = () =>
  import('@/views/settings/organisation/OrganisationPreferences.vue');
const FinanceSettings = () => import('@/views/settings/organisation/ChurchFinanceSettings.vue');
const OrganisationCommunicationSettings = () =>
  import(
    '@/views/settings/organisation/communication-settings/OrganisationCommunicationSettings.vue'
  );
const ChurchVerificationDocuments = () =>
  import('@/views/organisations/components/ChurchVerificationDocuments.vue');
const ChurchMembersAccounts = () =>
  import('@/views/organisations/components/ChurchMembersAccounts.vue');
const ChurchMembersProfiles = () =>
  import('@/views/organisations/components/ChurchMembersProfiles.vue');
const ChurchServices = () => import('@/views/organisations/components/ChurchServices.vue');
const ChurchVisitors = () => import('@/views/organisations/components/ChurchVisitors.vue');
const ChurchZones = () => import('@/views/organisations/components/ChurchZones.vue');
const ChurchMinistries = () => import('@/views/organisations/components/ChurchMinistries.vue');
const ChurchPositions = () => import('@/views/organisations/components/ChurchPositions.vue');
const ChurchSmsCenter = () => import('@/views/organisations/components/ChurchSmsCenter.vue');
const ChurchTemplatesLanding = () =>
  import('@/views/organisations/components/ChurchTemplatesLanding.vue');
const ChurchClaims = () => import('@/views/organisations/ChurchClaims.vue');
const ChurchClaimsDetails = () => import('@/views/organisations/ChurchClaimsDetails.vue');
// const ChurchHome = () => import('@/views/organisations/ChurchHome.vue');

// Communications
const SmsDashboard = () => import('@/views/communications/sms/SmsDashboard.vue');
const SmsTransaction = () => import('@/views/communications/sms/SmsTransaction.vue');
const SmsTransactions = () => import('@/views/communications/sms/SmsTransactions.vue');

//Membership
const InternalAccountRequests = () => import('@/views/members/InternalAccountRequests.vue');
const Users = () => import('@/views/members/Users.vue');
const User = () => import('@/views/members/User.vue');
const ChurchUserDetails = () => import('@/views/members/ChurchUserDetails.vue');
//const UserCreateForm = () => import('@/views/members/UserCreateForm.vue');
const ChurchUserEditForm = () => import('@/views/members/ChurchUserEditForm.vue');
const EditNonGlobalUserForm = () => import('@/views/members/EditNonGlobalUserForm.vue');
const UserProfiles = () => import('@/views/members/UserProfiles.vue');
const MembersBirthdays = () => import('@/views/members/MembersBirthdays.vue');
const UserProfile = () => import('@/views/members/UserProfile.vue');
const UserProfileCreateForm = () => import('@/views/members/MemberRegistrationForm.vue');
const ChurchMembershipRegistrationForm = () =>
  import('@/views/members/ChurchMembershipRegistrationForm.vue');
const SelfRegistrationLinks = () => import('@/views/members/SelfRegistrationLinks.vue');
const SelfRegistrationLink = () => import('@/views/members/SelfRegistrationLink.vue');
const MembershipRequests = () => import('@/views/members/MembershipRequests.vue');
const ContactDetailsAccessRequests = () =>
  import('@/views/members/ContactDetailsAccessRequests.vue');

// Socials
const ChurchFollowers = () => import('@/views/socials/ChurchFollowers.vue');
const ChurchTimeline = () => import('@/views/socials/post_system/ChurchTimeline.vue');

//Roles
const RolesTable = () => import('@/views/roles/RolesTable.vue');
const CreateRoleForm = () => import('@/views/roles/CreateRoleForm.vue');
const Role = () => import('@/views/roles/Role.vue');
const EditRoleForm = () => import('@/views/roles/EditRoleForm.vue');
const ViewRolePermissions = () => import('@/views/roles/ViewRolePermissions.vue');
const ViewRoleUsers = () => import('@/views/roles/ViewRoleUsers.vue');

// Permissions
const PermissionsTable = () => import('@/views/permissions/PermissionsTable.vue');
const CreatePermissionForm = () => import('@/views/permissions/CreatePermissionForm.vue');
const Permission = () => import('@/views/permissions/Permission.vue');
const EditPermissionForm = () => import('@/views/permissions/EditPermissionForm.vue');
const ViewPermissionUsers = () => import('@/views/permissions/ViewPermissionUsers.vue');
const ViewPermissionRoles = () => import('@/views/permissions/ViewPermissionRoles.vue');

// Logs
const LoginRecords = () => import('@/views/logs/LoginRecords.vue');
const Notifications = () => import('@/views/logs/Notifications.vue');

// Visitors
const VisitorRecords = () => import('@/views/visitors/VisitorsTable.vue');
const Visitor = () => import('@/views/visitors/Visitor.vue');
const VisitorEditForm = () => import('@/views/visitors/VisitorEditForm.vue');

// Zones
const ZoneRecords = () => import('@/views/zones/ZonesTable.vue');
const Zone = () => import('@/views/zones/Zone.vue');
const ZoneCreateForm = () => import('@/views/zones/ZoneCreateForm.vue');
const ZoneEditForm = () => import('@/views/zones/ZoneEditForm.vue');
const ZoneMembers = () => import('@/views/zones/ZoneMembersTable.vue');

// Positions
const PositionRecords = () => import('@/views/positions/Positions.vue');
const PositionMembers = () => import('@/views/positions/PositionMembers.vue');
const PositionEditForm = () => import('@/views/positions/PositionEditForm.vue');
const PositionCreationForm = () => import('@/views/positions/PositionCreationForm.vue');

// Fundraising
const FundraisingTable = () => import('@/views/fundraising/ChurchFundraisings.vue');
const FundraisingDetails = () => import('@/views/fundraising/FundraisingDetails.vue');
const FundraisingContributors = () => import('@/views/fundraising/FundraisingContributors.vue');

// Ministries
const MinistryRecords = () => import('@/views/ministries/MinistriesTable.vue');
const Ministry = () => import('@/views/ministries/Ministry.vue');
const MinistryEditForm = () => import('@/views/ministries/MinistryEditForm.vue');
const MinistryCreateForm = () => import('@/views/ministries/MinistryCreateForm.vue');
const MinistryMembers = () => import('@/views/ministries/MinistryMembersTable.vue');

// Groups
const Groups = () => import('@/views/groups/Groups.vue');
const Group = () => import('@/views/groups/Group.vue');
const GroupEditForm = () => import('@/views/groups/GroupEditForm.vue');
const GroupCreationForm = () => import('@/views/groups/GroupCreationForm.vue');
const GroupMembers = () => import('@/views/groups/GroupMembers.vue');

// Services
const Services = () => import('@/views/services/Services.vue');
const ServiceDashboard = () => import('@/views/services/ServiceDashboard.vue');
const Roster = () => import('@/views/services/Roster.vue');
const Rosters = () => import('@/views/services/Rosters.vue');
const ServiceBulletin = () => import('@/views/services/Bulletin.vue');
const ServiceVisitors = () => import('@/views/services/ServiceVisitors.vue');
const MassServiceCreationForm = () => import('@/views/services/MassServiceCreationForm.vue');
const ServiceVisitorsForm = () => import('@/views/services/CaptureServiceVisitors.vue');
const ServiceAttendees = () => import('@/views/services/ServiceAttendees.vue');
const ServiceAbsentees = () => import('@/views/services/ServiceAbsentees.vue');

// Member Settings
const Profile = () => import('@/views/settings/Profile.vue');
const EditProfile = () => import('@/views/settings/EditProfile.vue');
const MyAccountSettings = () => import('@/views/settings/MyAccountSettings.vue');

// Organisation settings
const MyOrganisation = () => import('@/views/settings/organisation/MyOrganisation.vue');
const OrganisationSettings = () => import('@/views/settings/organisation/OrganisationSettings.vue');

// System Settings
const GlobalSettings = () => import('@/views/settings/GlobalSettings.vue');

// Organisation templates
const OrganisationTemplatesLanding = () =>
  import('@/views/settings/organisation/organisation-templates/OrganisationTemplatesLanding.vue');

const ServiceActivitiesTemplateTable = () =>
  import(
    '@/views/settings/organisation/service-activities-templates/ServiceActivitiesTemplateTable.vue'
  );
const ServiceActivitiesTemplate = () =>
  import(
    '@/views/settings/organisation/service-activities-templates/ServiceActivitiesTemplate.vue'
  );
const EditServiceActivitiesTemplateForm = () =>
  import(
    '@/views/settings/organisation/service-activities-templates/EditServiceActivitiesTemplateForm.vue'
  );
const CreateServiceActivitiesTemplateForm = () =>
  import(
    '@/views/settings/organisation/service-activities-templates/CreateServiceActivitiesTemplateForm.vue'
  );

const ServiceActivities = () =>
  import(
    '@/views/settings/organisation/organisation-templates/service-activities/ServiceActivities.vue'
  );
const ServiceActivitiesCreationForm = () =>
  import(
    '@/views/settings/organisation/organisation-templates/service-activities/ServiceActivitiesCreationForm.vue'
  );
const ServiceActivityEditForm = () =>
  import(
    '@/views/settings/organisation/organisation-templates/service-activities/ServiceActivityEditForm.vue'
  );

const ServiceTypes = () =>
  import('@/views/settings/organisation/organisation-templates/service-types/ServiceTypes.vue');
const ServiceTypeCreationForm = () =>
  import(
    '@/views/settings/organisation/organisation-templates/service-types/ServiceTypeCreationForm.vue'
  );
const ServiceTypeEditForm = () =>
  import(
    '@/views/settings/organisation/organisation-templates/service-types/ServiceTypeEditForm.vue'
  );

const MembershipManners = () =>
  import(
    '@/views/settings/organisation/organisation-templates/membership-manners/MembershipManners.vue'
  );
const MembershipMannerEditForm = () =>
  import(
    '@/views/settings/organisation/organisation-templates/membership-manners/MembershipMannerEditForm.vue'
  );
const MembershipMannerCreationForm = () =>
  import(
    '@/views/settings/organisation/organisation-templates/membership-manners/MembershipMannerCreationForm.vue'
  );

const GivingTypes = () =>
  import('@/views/settings/organisation/organisation-templates/giving-types/GivingTypes.vue');
const GivingTypeEditForm = () =>
  import(
    '@/views/settings/organisation/organisation-templates/giving-types/GivingTypeEditForm.vue'
  );
const GivingTypeCreationForm = () =>
  import(
    '@/views/settings/organisation/organisation-templates/giving-types/GivingTypeCreationForm.vue'
  );

const SmsTemplates = () =>
  import('@/views/settings/organisation/organisation-templates/sms-templates/SmsTemplates.vue');
const SmsTemplateEditForm = () =>
  import(
    '@/views/settings/organisation/organisation-templates/sms-templates/SmsTemplateEditForm.vue'
  );
const SmsTemplateCreationForm = () =>
  import(
    '@/views/settings/organisation/organisation-templates/sms-templates/SmsTemplateCreationForm.vue'
  );

const RosterTemplates = () =>
  import('@/views/settings/organisation/organisation-templates/rosters/RosterTemplates.vue');
const EditRosterTemplate = () =>
  import('@/views/settings/organisation/organisation-templates/rosters/EditRosterTemplate.vue');
const CreateRosterTemplate = () =>
  import('@/views/settings/organisation/organisation-templates/rosters/CreateRosterTemplate.vue');

// Onboarding/Data Migration
const OnboardingDataMigrationLanding = () =>
  import('@/views/onboarding-data-migration/OnboardingDataMigrationLanding.vue');
const OnboardingDataMigrationStageOne = () =>
  import('@/views/onboarding-data-migration/OnboardingDataMigrationStageOne.vue');
const OnboardingDataMigrationStageTwo = () =>
  import('@/views/onboarding-data-migration/stage-two/OnboardingDataMigrationStageTwo.vue');

// Files
const DownloadableFiles = () => import('@/views/files/DownloadableFiles.vue');
const DownloadableFileCreateForm = () => import('@/views/files/DownloadableFileCreateForm.vue');

// Reports
const Reports = () => import('@/views/reports/Reports.vue');
const MembershipReports = () => import('@/views/reports/membership-reports/MembershipReports.vue');
const MembershipPlacementReports = () =>
  import('@/views/reports/membership-reports/MembershipPlacementReports.vue');
const AbsenteesReports = () => import('@/views/reports/membership-reports/AbsenteesReports.vue');

// Follow ups
const MemberFollowUps = () => import('@/views/follow-ups/MemberFollowUps.vue');
const AllMembershipFollowUps = () => import('@/views/follow-ups/AllMembershipFollowUps.vue');
const FollowUp = () => import('@/views/follow-ups/FollowUp.vue');
const MemberFollowUpCreationForm = () =>
  import('@/views/follow-ups/MemberFollowUpCreationForm.vue');

// Finances
// Financial Budgets
const FinancialBudgets = () => import('@/views/finances/budgets/FinancialBudgets.vue');
const FinancialBudget = () => import('@/views/finances/budgets/FinancialBudget.vue');
const FinancialBudgetCreationForm = () =>
  import('@/views/finances/budgets/FinancialBudgetCreationForm.vue');
const FinancialBudgetEditForm = () =>
  import('@/views/finances/budgets/FinancialBudgetEditForm.vue');
// Financial Requests
const FinancialRequestCreationForm = () =>
  import('@/views/finances/requests/FinancialRequestCreationForm.vue');
const FinancialRequestEditForm = () =>
  import('@/views/finances/requests/FinancialRequestEditForm.vue');
const FinancialRequest = () => import('@/views/finances/requests/FinancialRequest.vue');
const FinancialRequests = () => import('@/views/finances/requests/FinancialRequests.vue');

// Branch Management
const BranchManagementDashboard = () =>
  import('@/views/branch_management/BranchManagementDashboard.vue');
const PendingBranchManagementRequests = () =>
  import('@/views/branch_management/PendingBranchManagementRequests.vue');
const DenominationRecords = () => import('@/views/branch_management/DenominationRecords.vue');
const Denomination = () => import('@/views/branch_management/Denomination.vue');
const CreateDenominationChurchForm = () =>
  import('@/views/branch_management/CreateDenominationChurchForm.vue');
const BranchLevels = () => import('@/views/branch_management/BranchLevels.vue');
const DenominationLevelBranches = () =>
  import('@/views/branch_management/DenominationLevelBranches.vue');

// Mails
const MailCompose = () => import('@/views/mails/MailCompose.vue');

// Documentations
const ProductDocumentationsHome = () =>
  import('@/views/products_documentation/ProductDocumentationsHome.vue');
const DocsTopicsTable = () => import('@/views/products_documentation/DocsTopicsTable.vue');
const ArticleEditor = () => import('@/views/products_documentation/ArticleEditor.vue');
const ArticleVersions = () => import('@/views/products_documentation/ArticleVersions.vue');
const DocsCategoryLocalisations = () =>
  import('@/views/products_documentation/DocsCategoryLocalisations.vue');
const DocsArticlesTable = () => import('@/views/products_documentation/DocsArticlesTable.vue');

// Views - Pages
const Page404 = () => import('@/views/pages/Page404.vue');

const MembershipSelfRegistrationCheckpoint = () =>
  import('@/views/pages/MembershipSelfRegistrationCheckpoint.vue');
const MembershipSelfRegistrationForm = () =>
  import('@/views/pages/MembershipSelfRegistrationForm.vue');
const MembershipSelfRegistrationSuccess = () =>
  import('@/views/pages/MembershipSelfRegistrationSuccess.vue');

const AccountActivation = () => import('@/views/pages/AccountActivation.vue');

Vue.use(Router);

const router = new Router({
  mode: 'history', // https://router.vuejs.org/api/#mode
  linkActiveClass: 'open active',
  routes: [
    {
      path: '',
      redirect() {
        if (!isLoggedIn()) {
          return { name: 'Login' };
        }

        if (isGlobalUser()) {
          return isImpersonationActive()
            ? { name: 'ChurchDashboard', params: { churchId: churchId() } }
            : { name: 'GlobalDashboard' };
        }

        return { name: 'ChurchDashboard', params: { churchId: churchId() } };
      },
      name: 'Home',
      component: DefaultContainer,
      meta: {
        requiresAuth: true,
      },
      children: [
        {
          path: '/admin-dashboard-2f52gkl5',
          name: 'GlobalDashboard',
          component: Dashboard,
          beforeEnter: (to, from, next) => {
            if (!isLoggedIn()) {
              return next({ name: 'Login' });
            }

            if (isGlobalUser()) {
              return next();
            }

            next({ name: 'ChurchDashboard', params: { churchId: churchId() } });
          },
          meta: {
            showTitleBar: true,
            fullWidth: true,
            topActions: false,
            label: '$t__router_file.global_dashboard',
          },
        },

        {
          path: 'churches/:churchId',
          name: 'current_church',
          redirect: (to) => {
            // Important: since the route has a name, redirect
            // is needed so that the `ChurchDashboard` route is returned
            // if this route is called
            return { name: 'ChurchDashboard', params: to.params };
          },
          meta: {
            label: '$t__router_file.my_church',
            fullWidth: true,
            // Don't apply `routePermission` here
          },
          props: true,
          component: {
            render(c) {
              return c('router-view');
            },
          },
          children: [
            {
              path: 'dashboard',
              name: 'ChurchDashboard',
              component: ChurchDashboard,
              beforeEnter: (to, from, next) => {
                if (!isLoggedIn()) {
                  return next({ name: 'Login' });
                }

                if (isGlobalUser() && !isImpersonationActive()) {
                  return next({ name: 'GlobalDashboard' });
                }
                next();
              },
              meta: {
                showTitleBar: true,
                fullWidth: true,
                topActions: false,
                label: '$t__router_file.church_dashboard',
              },
            },

            /* {
              path: 'home',
              name: 'church_home',
              component: ChurchHome,
              meta: {
                label: `Church Home`,
              },
            }, */

            {
              path: 'members',
              name: 'church_members',
              redirect: (to) => {
                // Important: since the route has a name, redirect
                // is needed so that the `all_profiles` route is returned
                // if this route is called
                return { name: 'all_profiles', params: to.params };
              },
              meta: {
                label: '$t__router_file.members',
                routePermission: 'canListMembersProfiles',
              },
              component: {
                render(c) {
                  return c('router-view');
                },
              },
              children: [
                {
                  path: '',
                  component: UserProfiles,
                  name: 'all_profiles',
                  meta: {
                    label: '$t__router_file.all_members',
                    iconClasses: 'las la-users',
                    hasVueTable: true,
                    tableName: 'All_Members_Profiles',
                    scrollToLastViewedRow: true,
                    fullWidth: true,
                  },
                },
                {
                  path: 'register',
                  meta: {
                    label: '$t__router_file.register_a_new_member',
                    routePermission: 'canCreateMembersProfiles',
                    iconClasses: 'las la-user-plus',
                    fullWidth: true,
                  },
                  name: 'new_members_profile',
                  component: UserProfileCreateForm,
                },
                {
                  path: 'new',
                  redirect: 'register',
                },
                {
                  path: ':id/view',
                  meta: {
                    label: '$t__router_file.members_profile',
                    // `canListMembersProfiles` permission is used so that member who have been granted access to a member's details can access the view page. The backend still has 100% control of whether data is returned or not after viewing the member.
                    routePermission: 'canListMembersProfiles',
                    iconClasses: 'las la-user',
                    fullWidth: true,
                    showTitleBar: false,
                    useDefaultPageTopPadding: false,
                  },
                  name: 'view_members_profile',
                  component: UserProfile,
                },
                {
                  path: ':id/edit',
                  meta: {
                    label: '$t__router_file.edit_members_profile',
                    routePermission: 'canEditMembersProfiles',
                    iconClasses: 'las la-user-edit',
                    fullWidth: true,
                  },
                  name: 'edit_members_profile',
                  component: ChurchMembershipRegistrationForm,
                },
                {
                  path: 'membership-follow-ups',
                  meta: {
                    label: '$t__router_file.membership_follow_ups',
                    routePermission: 'canListFollowUps',
                    iconClasses: 'las la-comment',
                  },
                  component: {
                    render(c) {
                      return c('router-view');
                    },
                  },
                  children: [
                    {
                      path: '',
                      component: AllMembershipFollowUps,
                      name: 'all_membership_follow_ups',
                      meta: {
                        label: '$t__router_file.all_membership_follow_ups',
                        iconClasses: 'las la-comment',
                        fullWidth: true,
                      },
                    },
                    {
                      path: ':id/follow-ups',
                      component: MemberFollowUps,
                      name: 'member_follow_ups',
                      meta: {
                        label: '$t__router_file.members_follow_up_history',
                        iconClasses: 'las la-comment',
                        fullWidth: true,
                      },
                    },
                    {
                      path: ':id/follow-ups/create',
                      meta: {
                        label: '$t__router_file.create_members_follow_up',
                        routePermission: 'canCreateFollowUps',
                        iconClasses: 'las la-comment',
                      },
                      name: 'create_member_follow_up',
                      component: MemberFollowUpCreationForm,
                    },
                    {
                      path: ':follow_up_id/view',
                      meta: {
                        label: '$t__router_file.follow_up_details_or_thread',
                        routePermission: 'canViewFollowUps',
                        iconClasses: 'las la-comment',
                        pageClasses: 'col-xs-12 col-sm-8 col-md-10 col-lg-6 col-xl-4',
                      },
                      name: 'view_membership_follow_up',
                      component: FollowUp,
                    },
                  ],
                },
                {
                  path: 'self-registration-links',
                  meta: {
                    label: '$t__router_file.self_registration_links',
                    routePermission: 'canListMembersAccounts',
                    iconClasses: 'las la-link',
                  },
                  component: {
                    render(c) {
                      return c('router-view');
                    },
                  },
                  children: [
                    {
                      path: '',
                      component: SelfRegistrationLinks,
                      name: 'self_registration_links',
                      meta: {
                        label: '$t__router_file.all_self_registration_links',
                        iconClasses: 'las la-link',
                        fullWidth: true,
                      },
                    },
                    {
                      path: ':id/view',
                      meta: {
                        label: '$t__router_file.self_registration_link_details',
                        iconClasses: 'las la-link',
                      },
                      name: 'view_self_registration_link',
                      component: SelfRegistrationLink,
                      props: true,
                    },
                  ],
                },
                {
                  path: 'membership-requests/:requestType',
                  name: 'membership_requests',
                  meta: {
                    label: '$t__router_file.membership_requests',
                    routePermission: 'canListMembersAccounts',
                    iconClasses: 'las la-user-check',
                    fullWidth: true,
                  },
                  component: MembershipRequests,
                  props: true,
                },
                {
                  path: 'contact-details-access-requests',
                  name: 'contact_details_access_requests',
                  meta: {
                    label: '$t__router_file.contact_details_access_requests',
                    routePermission: 'canApproveContactDetailsAccesses',
                    iconClasses: 'las la-user-check',
                    fullWidth: true,
                  },
                  component: ContactDetailsAccessRequests,
                },
              ],
            },

            {
              path: 'members-accounts',
              meta: {
                label: '$t__router_file.members_accounts',
                routePermission: 'canListMembersAccounts',
              },
              component: {
                render(c) {
                  return c('router-view');
                },
              },
              children: [
                {
                  path: 'church-admins',
                  component: {
                    render(c) {
                      return c('router-view');
                    },
                  },
                  children: [
                    {
                      path: '',
                      component: () => import('../views/members/ChurchAdmins.vue'),
                      name: 'church_admins',
                      meta: {
                        label: '$t__router_file.church_administrators',
                        iconClasses: 'las la-user-shield',
                        fullWidth: true,
                      },
                    },
                    {
                      path: 'pending-invites',
                      component: () => import('../views/members/PendingChurchAdmins.vue'),
                      name: 'church_admins_invitations',
                      meta: {
                        label: '$t__router_file.pending_admin_invitations',
                        iconClasses: 'las la-user-shield',
                        fullWidth: true,
                      },
                    },
                  ],
                },
                {
                  path: 'internal-account-requests',
                  component: InternalAccountRequests,
                  name: 'internal_account_requests',
                  meta: {
                    label: '$t__router_file.internal_account_requests',
                    iconClasses: 'las la-users',
                    fullWidth: true,
                  },
                },
                {
                  path: ':userId/view',
                  meta: {
                    label: '$t__router_file.members_account',
                    routePermission: 'canViewMembersAccounts',
                    iconClasses: 'las la-user',
                  },
                  name: 'view_user',
                  component: ChurchUserDetails,
                  props: true,
                },
                {
                  path: ':userId/edit',
                  meta: {
                    label: '$t__router_file.members_account_form',
                    routePermission: 'canEditMembersAccounts',
                    iconClasses: 'las la-user-edit',
                  },
                  name: 'edit_user',
                  component: ChurchUserEditForm,
                  props: true,
                },
              ],
            },

            {
              path: 'services',
              meta: {
                label: '$t__router_file.services',
                routePermission: 'canListServices',
              },
              component: {
                render(c) {
                  return c('router-view');
                },
              },
              children: [
                {
                  path: '',
                  name: 'all_services',
                  component: Services,
                  meta: {
                    label: '$t__router_file.worship_services',
                    iconClasses: 'las la-church',
                    fullWidth: true,
                  },
                },
                {
                  path: 'mass-creation',
                  name: 'mass_create_service',
                  component: MassServiceCreationForm,
                  meta: {
                    label: '$t__router_file.worship_services_creation_form',
                    routePermission: 'canCreateServices',
                    iconClasses: 'las la-plus-circle',
                  },
                },
                {
                  path: ':id/view',
                  name: 'view_service',
                  component: ServiceDashboard,
                  meta: {
                    label: '$t__router_file.worship_service_dashboard',
                    routePermission: 'canViewServices',
                    iconClasses: 'las la-church',
                    fullWidth: true,
                  },
                },
                {
                  path: ':id/bulletin',
                  name: 'view_service_bulletin',
                  component: ServiceBulletin,
                  meta: {
                    label: '$t__router_file.service_bulletin',
                    routePermission: 'canViewServices',
                    iconClasses: 'las la-clipboard-list',
                    fullWidth: true,
                  },
                },

                {
                  path: ':id/visitors/capture',
                  name: 'capture_service_visitors',
                  component: ServiceVisitorsForm,
                  props: true,
                  meta: {
                    label: '$t__router_file.capture_service_visitors',
                    routePermission: 'canCreateVisitors',
                    iconClasses: 'las la-plus-circle',
                  },
                },
                {
                  path: ':id/attendees',
                  name: 'view_attendees',
                  component: ServiceAttendees,
                  meta: {
                    label: '$t__router_file.service_attendees',
                    routePermission: 'canViewAttendances',
                    iconClasses: 'las la-users',
                    fullWidth: true,
                  },
                },
                {
                  path: ':id/absentees',
                  name: 'view_absentees',
                  component: ServiceAbsentees,
                  meta: {
                    label: '$t__router_file.service_absentees',
                    routePermission: 'canViewAttendances',
                    iconClasses: 'las la-users',
                    fullWidth: true,
                  },
                },
                {
                  path: ':id/visitors',
                  name: 'view_service_visitors',
                  component: ServiceVisitors,
                  meta: {
                    label: '$t__router_file.view_service_visitors',
                    routePermission: 'canViewVisitors',
                    iconClasses: 'las la-users',
                    fullWidth: true,
                  },
                },
              ],
            },

            {
              path: 'rosters',
              meta: {
                label: '$t__router_file.rosters',
                routePermission: 'canListRosters',
                iconClasses: 'las la-calendar',
              },
              component: {
                render(c) {
                  return c('router-view');
                },
              },
              children: [
                {
                  path: '',
                  name: 'rosters',
                  component: Rosters,
                  meta: {
                    label: '$t__router_file.monthly_service_activities_roster',
                    iconClasses: 'las la-calendar',
                    fullWidth: true,
                  },
                },
                {
                  path: ':month/:year',
                  name: 'roster_monthly',
                  component: Roster,
                  props: true,
                  meta: {
                    fullWidth: true,
                    label: '$t__router_file.monthly_roster_Details',
                    routePermission: 'canViewRosters',
                    iconClasses: 'las la-calendar',
                  },
                },
              ],
            },

            {
              path: 'visitors',
              meta: {
                label: '$t__router_file.visitors',
                routePermission: 'canListVisitors',
              },
              component: {
                render(c) {
                  return c('router-view');
                },
              },
              children: [
                {
                  path: '',
                  name: 'all_visitors',
                  component: VisitorRecords,
                  meta: {
                    label: '$t__router_file.all_visitors',
                    iconClasses: 'las la-user-friends',
                    fullWidth: true,
                  },
                },
                {
                  path: ':id/edit',
                  name: 'edit_visitor',
                  component: VisitorEditForm,
                  props: true,
                  meta: {
                    label: '$t__router_file.edit_visitor',
                    routePermission: 'canEditVisitors',
                    iconClasses: 'las la-edit',
                  },
                },
                {
                  path: ':id/view',
                  name: 'view_visitor',
                  component: Visitor,
                  meta: {
                    label: '$t__router_file.visitor_details',
                    routePermission: 'canViewVisitors',
                    iconClasses: 'las la-user-friends',
                  },
                },
              ],
            },

            {
              path: 'zones',
              meta: {
                label: '$t__router_file.fellowship_zones',
                routePermission: 'canListZones',
              },
              component: {
                render(c) {
                  return c('router-view');
                },
              },
              children: [
                {
                  path: '',
                  name: 'all_zones',
                  component: ZoneRecords,
                  meta: {
                    label: '$t__router_file.all_fellowship_zones',
                    iconClasses: 'las la-share-alt',
                    fullWidth: true,
                  },
                },
                {
                  path: 'new',
                  name: 'add_zone',
                  component: ZoneCreateForm,
                  meta: {
                    label: '$t__router_file.create_new_fellowship_zone',
                    routePermission: 'canCreateZones',
                    iconClasses: 'las la-plus-circle',
                  },
                },
                {
                  path: ':id/edit',
                  name: 'edit_zone',
                  component: ZoneEditForm,
                  meta: {
                    label: '$t__router_file.edit_fellowship_zone',
                    routePermission: 'canEditZones',
                    iconClasses: 'las la-edit',
                  },
                },
                {
                  path: ':id/view',
                  name: 'view_zone',
                  component: Zone,
                  meta: {
                    label: '$t__router_file.fellowship_zone_details',
                    routePermission: 'canViewZones',
                    iconClasses: 'las la-share-alt',
                  },
                },
                {
                  path: ':id/members',
                  name: 'view_zone_members',
                  component: ZoneMembers,
                  meta: {
                    label: '$t__router_file.fellowship_zone_members',
                    routePermission: 'canEditZones',
                    iconClasses: 'las la-share-alt',
                    fullWidth: true,
                  },
                },
              ],
            },

            {
              path: 'ministries',
              meta: {
                label: '$t__router_file.ministries',
                routePermission: 'canListMinistries',
              },
              component: {
                render(c) {
                  return c('router-view');
                },
              },
              children: [
                {
                  path: '',
                  name: 'all_ministries',
                  component: MinistryRecords,
                  meta: {
                    label: '$t__router_file.all_ministries',
                    iconClasses: 'las la-people-carry',
                    fullWidth: true,
                  },
                },
                {
                  path: 'new',
                  name: 'add_ministry',
                  component: MinistryCreateForm,
                  meta: {
                    label: '$t__router_file.create_new_ministry',
                    routePermission: 'canCreateMinistries',
                    iconClasses: 'las la-plus-circle',
                  },
                },
                {
                  path: ':id/edit',
                  name: 'edit_ministry',
                  component: MinistryEditForm,
                  meta: {
                    label: '$t__router_file.edit_ministry',
                    routePermission: 'canEditMinistries',
                    iconClasses: 'las la-edit',
                  },
                },
                {
                  path: ':id/view',
                  name: 'view_ministry',
                  component: Ministry,
                  meta: {
                    label: '$t__router_file.ministry_details',
                    routePermission: 'canViewMinistries',
                    iconClasses: 'las la-people-carry',
                  },
                },
                {
                  path: ':id/members',
                  name: 'view_ministry_members',
                  component: MinistryMembers,
                  meta: {
                    label: '$t__router_file.view_ministry_members',
                    routePermission: 'canEditMinistries',
                    iconClasses: 'las la-users',
                    fullWidth: true,
                  },
                },
                {
                  path: ':id/financial-budgets/new',
                  name: 'new_ministry_financial_budget',
                  component: FinancialBudgetCreationForm,
                  props: true,
                  meta: {
                    label: '$t__router_file.create_financial_budget_for_ministry',
                    routePermission: ['canCreateFinancialBudgets'],
                    iconClasses: 'las la-users',
                  },
                },
                {
                  path: ':id/financial-requests/new',
                  name: 'new_ministry_financial_request',
                  component: FinancialRequestCreationForm,
                  props: true,
                  meta: {
                    label: '$t__router_file.create_financial_request_for_ministry',
                    routePermission: ['canCreateFinancialRequests'],
                    iconClasses: 'las la-users',
                  },
                },
              ],
            },

            {
              path: 'groups',
              meta: {
                label: '$t__router_file.groups',
                routePermission: 'canListGroups',
              },
              component: {
                render(c) {
                  return c('router-view');
                },
              },
              children: [
                {
                  path: '',
                  name: 'all_groups',
                  component: Groups,
                  meta: {
                    label: '$t__router_file.all_groups',
                    iconClasses: 'las la-users',
                    fullWidth: true,
                  },
                },
                {
                  path: 'new',
                  name: 'add_group',
                  component: GroupCreationForm,
                  meta: {
                    label: '$t__router_file.create_new_group',
                    routePermission: 'canCreateGroups',
                    iconClasses: 'las la-plus-circle',
                  },
                },
                {
                  path: ':id/edit',
                  name: 'edit_group',
                  component: GroupEditForm,
                  meta: {
                    label: '$t__router_file.edit_group',
                    routePermission: 'canEditGroups',
                    iconClasses: 'las la-edit',
                  },
                },
                {
                  path: ':id/view',
                  name: 'view_group',
                  component: Group,
                  meta: {
                    label: '$t__router_file.view_group',
                    routePermission: 'canViewGroups',
                    iconClasses: 'las la-user-friends',
                  },
                },
                {
                  path: ':id/members',
                  name: 'view_group_members',
                  component: GroupMembers,
                  meta: {
                    label: '$t__router_file.group_members',
                    routePermission: 'canEditGroups',
                    iconClasses: 'las la-users',
                    fullWidth: true,
                  },
                },
              ],
            },

            {
              path: 'positions',
              meta: {
                label: '$t__router_file.positions',
                routePermission: 'canListPositions',
                iconClasses: 'las la-sitemap',
              },
              component: {
                render(c) {
                  return c('router-view');
                },
              },
              children: [
                {
                  path: '',
                  name: 'all_positions',
                  component: PositionRecords,
                  meta: {
                    label: '$t__router_file.all_positions',
                    iconClasses: 'las la-sitemap',
                    fullWidth: true,
                  },
                },
                {
                  path: 'new',
                  name: 'add_position',
                  component: PositionCreationForm,
                  meta: {
                    label: '$t__router_file.create_new_position',
                    routePermission: 'canCreatePositions',
                    iconClasses: 'las la-plus-circle',
                  },
                },
                {
                  path: ':id/edit',
                  name: 'edit_position',
                  component: PositionEditForm,
                  meta: {
                    label: '$t__router_file.edit_position',
                    routePermission: 'canEditPositions',
                    iconClasses: 'las la-edit',
                  },
                },
                {
                  path: ':id/members',
                  name: 'view_position_members',
                  component: PositionMembers,
                  meta: {
                    label: '$t__router_file.position_members',
                    routePermission: 'canViewMembersProfiles',
                    iconClasses: 'las la-sitemap',
                    fullWidth: true,
                  },
                },
              ],
            },
            {
              path: 'fundraisings',
              meta: {
                label: '$t__router_file.fundraisings',
                routePermission: 'canListFundraisings',
                iconComponent: 'HandHoldingHeartIcon',
              },
              component: {
                render(c) {
                  return c('router-view');
                },
              },
              children: [
                {
                  path: '',
                  name: 'all_fundraisings',
                  component: FundraisingTable,
                  meta: {
                    label: '$t__router_file.fundraisings',
                    iconComponent: 'HandHoldingHeartIcon',
                    fullWidth: true,
                  },
                },
                {
                  path: ':id/view',
                  name: 'view_fundraising',
                  component: FundraisingDetails,
                  meta: {
                    label: '$t__router_file.fundraising_details',
                    routePermission: 'canViewFundraisings',
                    iconComponent: 'HandHoldingHeartIcon',
                    fullWidth: true,
                  },
                },
                {
                  path: ':id/contributors',
                  name: 'fundraising_contributors',
                  component: FundraisingContributors,
                  meta: {
                    label: '$t__router_file.fundraising_contributors',
                    routePermission: 'canViewFundraisings',
                    iconComponent: 'HandHoldingHeartIcon',
                    fullWidth: true,
                  },
                },
              ],
            },

            {
              path: 'finances',
              meta: {
                label: '$t__router_file.finances',
                routePermission: ['canListFinancialBudgets', 'canListFinancialRequests'],
              },
              component: {
                render(c) {
                  return c('router-view');
                },
              },
              children: [
                {
                  path: 'financial-budgets',
                  meta: {
                    label: '$t__router_file.financial_budgets',
                    routePermission: 'canListFinancialBudgets',
                  },
                  component: {
                    render(c) {
                      return c('router-view');
                    },
                  },
                  children: [
                    {
                      path: '',
                      name: 'all_financial_budgets',
                      component: FinancialBudgets,
                      meta: {
                        label: '$t__router_file.all_financial_budgets',
                        iconClasses: 'la la-file-invoice-dollar',
                        fullWidth: true,
                      },
                    },
                    {
                      path: 'new',
                      name: 'create_financial_budget',
                      component: FinancialBudgetCreationForm,
                      meta: {
                        label: '$t__router_file.create_a_financial_budget',
                        routePermission: 'canCreateFinancialBudgets',
                        pageClasses: 'col-lg-7 col-md-10 col-xs-12',
                      },
                    },
                    {
                      path: ':id/view',
                      name: 'view_financial_budget',
                      component: FinancialBudget,
                      meta: {
                        label: '$t__router_file.view_financial_budget',
                        routePermission: 'canViewFinancialBudgets',
                        fullWidth: true,
                      },
                    },
                    {
                      path: ':id/edit',
                      name: 'edit_financial_budget',
                      component: FinancialBudgetEditForm,
                      meta: {
                        label: '$t__router_file.edit_financial_budget',
                        routePermission: 'canViewFinancialBudgets',
                        iconClasses: 'la la-file-invoice-dollar',
                        pageClasses: 'col-lg-7 col-md-10 col-xs-12',
                      },
                    },
                  ],
                },
                {
                  path: 'financial-requests',
                  meta: {
                    label: '$t__router_file.financial_requests',
                    routePermission: 'canListFinancialRequests',
                  },
                  component: {
                    render(c) {
                      return c('router-view');
                    },
                  },
                  children: [
                    {
                      path: '',
                      name: 'all_financial_requests',
                      component: FinancialRequests,
                      meta: {
                        label: '$t__router_file.all_financial_requests',
                        iconClasses: 'la la-file-invoice-dollar',
                        fullWidth: true,
                      },
                    },
                    {
                      path: 'new',
                      name: 'create_financial_request',
                      component: FinancialRequestCreationForm,
                      meta: {
                        label: '$t__router_file.create_a_financial_request',
                        routePermission: 'canCreateFinancialRequests',
                        pageClasses: 'col-lg-7 col-md-10 col-xs-12',
                      },
                    },
                    {
                      path: ':id/view',
                      name: 'view_financial_request',
                      component: FinancialRequest,
                      meta: {
                        label: '$t__router_file.view_financial_request',
                        routePermission: 'canViewFinancialRequests',
                        fullWidth: true,
                      },
                    },
                    {
                      path: ':id/edit',
                      name: 'edit_financial_request',
                      component: FinancialRequestEditForm,
                      meta: {
                        label: '$t__router_file.edit_financial_request',
                        routePermission: 'canViewFinancialRequests',
                        iconClasses: 'la la-file-invoice-dollar',
                        pageClasses: 'col-lg-7 col-md-10 col-xs-12',
                      },
                    },
                  ],
                },
                {
                  path: 'payments',
                  meta: {
                    label: '$t__router_file.payments',
                    routePermission: 'canListFinancialRequests',
                  },
                  component: {
                    render(c) {
                      return c('router-view');
                    },
                  },
                  redirect(to) {
                    return { ...to, name: 'payment_dashboard' };
                  },
                  children: [
                    {
                      path: 'dashboard',
                      name: 'payment_dashboard',
                      component: () => import('@/views/payments/PaymentDashboard.vue'),
                      meta: {
                        label: '$t__router_file.payment_dashboard',
                        iconClasses: 'las la-receipt',
                        fullWidth: true,
                        useDefaultPageTopPadding: false,
                        /**
                         * @todo Remove before release
                         */
                        routePermission: inDevelopment,
                      },
                    },

                    {
                      path: 'payment-application',
                      name: 'application_for_payment_account',
                      component: () => import('@/views/payments/PaymentApplication.vue'),
                      meta: {
                        label: '$t__router_file.payment_application',
                        iconClasses: 'las la-receipt',
                        fullWidth: true,
                        useDefaultPageTopPadding: false,
                        /**
                         * @todo Remove before release
                         */
                        routePermission: inDevelopment,
                      },
                    },

                    {
                      path: 'payment-withdrawal',
                      name: 'withdrawal',
                      component: () => import('@/views/payments/PaymentWithdrawal.vue'),
                      meta: {
                        label: '$t__router_file.payment_withdrawal',
                        iconClasses: 'las la-receipt',
                        fullWidth: true,
                        useDefaultPageTopPadding: false,
                        /**
                         * @todo Remove before release
                         */
                        routePermission: inDevelopment,
                      },
                    },

                    {
                      path: 'fundraising-payments',
                      name: 'fundraising_payments',
                      component: () => import('@/views/payments/AllPayments.vue'),
                      meta: {
                        label: '$t__router_file.fundraising_payments',
                        iconClasses: 'las la-receipt',
                        fullWidth: true,
                        useDefaultPageTopPadding: false,
                      },
                    },
                  ],
                },
              ],
            },

            {
              path: 'reports',
              meta: {
                label: '$t__router_file.reports',
                routePermission: 'canDownloadOrPrintReports',
              },
              component: {
                render(c) {
                  return c('router-view');
                },
              },
              children: [
                {
                  path: '',
                  name: 'all_church_reports',
                  component: Reports,
                  meta: {
                    label: '$t__router_file.all_church_reports',
                    iconClasses: 'las la-folder-open',
                  },
                },
                {
                  path: 'membership-reports',
                  component: {
                    render(c) {
                      return c('router-view');
                    },
                  },
                  meta: {
                    label: '$t__router_file.membership_reports',
                  },
                  children: [
                    {
                      path: '',
                      component: MembershipReports,
                      name: 'all_membership_reports',
                      meta: {
                        label: '$t__router_file.all_membership_reports',
                        fullWidth: true,
                      },
                    },
                    {
                      path: 'absentees-reports',
                      meta: {
                        label: '$t__router_file.absentees_reports',
                      },
                      component: {
                        render(c) {
                          return c('router-view');
                        },
                      },
                      children: [
                        {
                          path: '',
                          component: AbsenteesReports,
                          name: 'service_absentees_reports',
                          meta: {
                            label: '$t__router_file.all_absentees_reports',
                            iconClasses: 'las la-file-alt',
                            fullWidth: true,
                          },
                        },
                      ],
                    },
                    {
                      path: 'membership-reports',
                      component: MembershipPlacementReports,
                      name: 'membership_reports',
                      meta: {
                        label: '$t__router_file.membership_reports',
                        iconClasses: 'las la-file-alt',
                        fullWidth: true,
                      },
                    },
                    {
                      path: 'birthdays',
                      meta: {
                        label: '$t__router_file.members_birthdays',
                        routePermission: 'canDownloadOrPrintReports',
                      },
                      component: {
                        render(c) {
                          return c('router-view');
                        },
                      },
                      children: [
                        {
                          path: '',
                          component: MembersBirthdays,
                          name: 'all_birthdays',
                          meta: {
                            label: '$t__router_file.all_members_birthdays',
                            iconClasses: 'las la-birthday-cake',
                            fullWidth: true,
                          },
                        },
                      ],
                    },
                  ],
                },
              ],
            },

            {
              path: 'communications',
              meta: {
                label: '$t__router_file.communications',
                routePermission: 'canViewSmsSettings',
              },
              component: {
                render(c) {
                  return c('router-view');
                },
              },
              children: [
                {
                  path: 'sms-center',
                  meta: {
                    label: '$t__router_file.sms_center',
                    routePermission: 'canViewSmsSettings',
                  },
                  component: {
                    render(c) {
                      return c('router-view');
                    },
                  },
                  children: [
                    {
                      path: '',
                      name: 'sms_dashboard',
                      component: SmsDashboard,
                      meta: {
                        label: '$t__router_file.sms_dashboard',
                        iconClasses: 'las la-tachometer-alt',
                        fullWidth: true,
                        topActions: false,
                      },
                    },
                    {
                      path: 'sms-transactions',
                      component: {
                        render(c) {
                          return c('router-view');
                        },
                      },
                      meta: {
                        label: '$t__router_file.sms_transactions',
                      },
                      children: [
                        {
                          path: '',
                          component: SmsTransactions,
                          name: 'all_sms_transactions',
                          meta: {
                            label: '$t__router_file.all_sms_transactions',
                            iconClasses: 'las la-stream',
                            fullWidth: true,
                          },
                        },
                        {
                          path: ':id/view',
                          component: SmsTransaction,
                          name: 'view_sms_transaction',
                          meta: {
                            label: '$t__router_file.sms_transaction_details',
                            routePermission: 'canViewSmsSettings',
                            iconClasses: 'las la-stream',
                          },
                        },
                      ],
                    },
                  ],
                },
              ],
            },

            {
              path: 'settings',
              meta: {
                label: '$t__router_file.settings',
                routePermission: 'canEditSelf',
              },
              component: {
                render(c) {
                  return c('router-view');
                },
              },
              children: [
                {
                  path: 'my-profile',
                  meta: {
                    label: '$t__router_file.my_church_profile',
                    routePermission: 'canEditSelf',
                    fullWidth: true,
                  },
                  component: {
                    render(c) {
                      return c('router-view');
                    },
                  },
                  children: [
                    {
                      path: '',
                      name: 'my_church_profile',
                      props: true,
                      component: Profile,
                      meta: {
                        label: '$t__router_file.my_church_profile',
                        routePermission: 'canEditSelf',
                        iconClasses: 'las la-user',
                        fullWidth: true,
                        useDefaultPageTopPadding: false,
                      },
                    },
                    {
                      path: 'edit',
                      name: 'my_profile_edit',
                      props: true,
                      component: EditProfile,
                      meta: {
                        label: '$t__router_file.editing_your_church_profile',
                        routePermission: 'canEditSelf',
                        iconClasses: 'las la-user-edit',
                        fullWidth: true,
                      },
                    },
                  ],
                },

                {
                  path: 'church-profile/:tab/:innerTab?',
                  name: 'my_organisation',
                  component: MyOrganisation,
                  props: true,
                  meta: {
                    label: '$t__router_file.my_church_details',
                    routePermission: 'canViewOrganisationSettings',
                    iconClasses: 'las la-church',
                    fullWidth: true,
                    showTitleBar: false,
                    useDefaultPageTopPadding: false,
                  },
                },
                {
                  path: 'church-settings',
                  name: 'edit_organisation_settings',
                  props: true,
                  component: OrganisationSettings,
                  redirect(to) {
                    return {
                      name: 'church_details_settings',
                      params: { ...to.params },
                    };
                  },
                  meta: {
                    label: '$t__router_file.my_church_settings',
                    routePermission: 'canEditOrganisationSettings',
                    iconClasses: 'las la-cog',
                  },
                  children: [
                    {
                      path: 'church-details',
                      name: 'church_details_settings',
                      props: true,
                      component: OrganisationEditForm,
                      meta: {
                        label: '$t__router_file.edit_your_church_details',
                        iconClasses: 'las la-cog',
                        fullWidth: true,
                      },
                    },
                    {
                      path: 'church-verification-docs',
                      name: 'church_verification_docs_settings',
                      props: true,
                      component: OrganisationVerificationDocs,
                      meta: {
                        label: '$t__router_file.edit_your_church_verification_documents',
                        iconClasses: 'las la-cog',
                        fullWidth: true,
                      },
                    },
                    {
                      path: 'church-preferences',
                      name: 'church_preferences_settings',
                      props: true,
                      component: OrganisationPreferences,
                      meta: {
                        label: '$t__router_file.edit_your_church_preferences',
                        iconClasses: 'las la-cog',
                        fullWidth: true,
                      },
                    },
                    {
                      path: 'church-communication-settings',
                      name: 'church_communication_settings',
                      props: true,
                      component: OrganisationCommunicationSettings,
                      meta: {
                        label: '$t__router_file.edit_your_church_communication_settings',
                        iconClasses: 'las la-cog',
                        fullWidth: true,
                      },
                    },
                    {
                      path: 'church-finance-settings',
                      name: 'church_finance_settings',
                      props: true,
                      component: FinanceSettings,
                      meta: {
                        label: '$t__router_file.edit_your_church_finance_settings',
                        iconClasses: 'las la-cog',
                        fullWidth: true,
                      },
                    },
                    {
                      path: 'worship-services-settings',
                      name: 'worship_services_settings',
                      props: true,
                      component: () =>
                        import('../views/settings/organisation/WorshipServicesSettings.vue'),
                      meta: {
                        label: '$t__router_file.edit_your_church_worship_services_settings',
                        iconClasses: 'las la-cog',
                        fullWidth: true,
                      },
                    },
                    {
                      path: 'church-social-media-handles',
                      name: 'church_social_media_handles',
                      props: true,
                      component: () =>
                        import('../views/settings/organisation/ChurchSocialMediaHandles.vue'),
                      meta: {
                        label: '$t__router_file.church_social_media_handles',
                        iconClasses: 'las la-cog',
                        fullWidth: true,
                      },
                    },
                    {
                      path: 'subscription',
                      name: 'subscription',
                      props: true,
                      component: () =>
                        import('../views/settings/organisation/pricing/PricingPage.vue'),
                      meta: {
                        label: '$t__router_file.gotedo_vineyard_subscription',
                        iconClasses: 'las la-cog',
                        fullWidth: true,
                      },
                    },
                    {
                      path: 'subscription/:tierPricingId/new',
                      name: 'new_subscription_billing',
                      props: true,

                      component: () =>
                        import('../views/settings/organisation/pricing/CreateSubscription.vue'),
                      meta: {
                        label: '$t__router_file.gotedo_vineyard_subscription',
                        iconClasses: 'las la-cog',
                        fullWidth: true,
                        routePermission: 'canListMakeSubscriptionPayments',
                      },
                    },
                    {
                      path: 'billing',
                      name: 'billing',
                      props: true,
                      component: () =>
                        import('../views/settings/organisation/pricing/BillingHistoryPage.vue'),
                      meta: {
                        label: '$t__router_file.billing_history',
                        iconClasses: 'las la-cog',
                        fullWidth: true,
                      },
                    },
                    {
                      path: 'payment-methods',
                      name: 'payment_methods',
                      props: true,
                      component: () =>
                        import('../views/settings/organisation/pricing/PaymentMethods.vue'),
                      meta: {
                        label: '$t__router_file.payment_methods',
                        iconClasses: 'las la-cog',
                        fullWidth: true,
                        canListPaymentMethods: 'canViewOrganisationSettings',
                      },
                    },
                  ],
                },
                {
                  path: 'church-templates',
                  props: true,
                  meta: {
                    label: '$t__router_file.church_templates',
                    routePermission: 'canViewOrganisationSettings',
                  },
                  component: {
                    render(c) {
                      return c('router-view');
                    },
                  },
                  children: [
                    {
                      path: '',
                      name: 'organisation_templates',
                      props: true,
                      component: OrganisationTemplatesLanding,
                      meta: {
                        label: '$t__router_file.all_church_templates',
                        iconClasses: 'las la-file-alt',
                        fullWidth: true,
                      },
                    },
                    {
                      path: 'service-activities-templates',
                      props: true,
                      meta: {
                        label: '$t__router_file.service_activities_templates',
                        routePermission: 'canListServiceActivitiesTemplates',
                        iconClasses: 'las la-file-alt',
                      },
                      component: {
                        render(c) {
                          return c('router-view');
                        },
                      },
                      children: [
                        {
                          path: '',
                          name: 'all_service_activities_templates',
                          props: true,
                          component: ServiceActivitiesTemplateTable,
                          meta: {
                            label: '$t__router_file.all_service_activities_templates',
                            iconClasses: 'las la-file-alt',
                            fullWidth: true,
                          },
                        },
                        {
                          path: ':id/view',
                          name: 'view_service_activities_template',
                          props: true,
                          component: ServiceActivitiesTemplate,
                          meta: {
                            label: '$t__router_file.view_service_activities_template',
                            routePermission: 'canViewServiceActivitiesTemplates',
                            iconClasses: 'las la-file-alt',
                            fullWidth: true,
                          },
                        },
                        {
                          path: ':id/edit',
                          name: 'edit_service_activities_template',
                          props: true,
                          component: EditServiceActivitiesTemplateForm,
                          meta: {
                            label: '$t__router_file.edit_service_activities_template',
                            routePermission: 'canEditServiceActivitiesTemplates',
                            iconClasses: 'las la-file-alt',
                          },
                        },
                        {
                          path: 'new',
                          name: 'add_service_activities_template',
                          props: true,
                          component: CreateServiceActivitiesTemplateForm,
                          meta: {
                            label: '$t__router_file.create_service_activities_template',
                            routePermission: 'canCreateServiceActivitiesTemplates',
                            iconClasses: 'las la-file-alt',
                          },
                        },
                      ],
                    },
                    {
                      path: 'service-activities',
                      meta: {
                        label: '$t__router_file.service_activities',
                        routePermission: 'canListServiceActivitiesTemplates',
                        iconClasses: 'las la-file-alt',
                      },
                      component: {
                        render(c) {
                          return c('router-view');
                        },
                      },
                      children: [
                        {
                          path: '',
                          name: 'all_service_activities',
                          component: ServiceActivities,
                          meta: {
                            label: '$t__router_file.all_service_activities',
                            iconClasses: 'las la-file-alt',
                            fullWidth: true,
                          },
                        },
                        {
                          path: ':id/edit',
                          name: 'edit_service_activities',
                          component: ServiceActivityEditForm,
                          meta: {
                            label: '$t__router_file.edit_service_activity',
                            routePermission: 'canEditServiceActivitiesTemplates',
                            iconClasses: 'las la-edit',
                            pageClasses: 'col-xl-10 col-lg-10 col-xs-12',
                            hideScrollToTop: true,
                          },
                        },
                        {
                          path: 'new',
                          name: 'add_service_activities',
                          component: ServiceActivitiesCreationForm,
                          meta: {
                            label: '$t__router_file.create_service_activities',
                            routePermission: 'canCreateServiceActivitiesTemplates',
                            iconClasses: 'las la-plus-circle',
                            pageClasses: 'col-xl-10 col-lg-10 col-xs-12',
                            hideScrollToTop: true,
                          },
                        },
                      ],
                    },
                    {
                      path: 'roster-templates',
                      meta: {
                        label: '$t__router_file.roster_templates',
                        routePermission: 'canListServiceActivitiesTemplates',
                        iconClasses: 'las la-file-alt',
                      },
                      component: {
                        render(c) {
                          return c('router-view');
                        },
                      },
                      children: [
                        {
                          path: '',
                          name: 'all_roster_templates',
                          component: RosterTemplates,
                          meta: {
                            label: '$t__router_file.all_roster_templates',
                            iconClasses: 'las la-file-alt',
                            fullWidth: true,
                          },
                        },
                        {
                          path: ':id/view',
                          name: 'view_roster_template',
                          //component: RosterTemplate,
                          meta: {
                            label: '$t__router_file.view_roster_template',
                            routePermission: 'canViewServiceActivitiesTemplates',
                            iconClasses: 'las la-file-alt',
                          },
                        },
                        {
                          path: ':id/edit',
                          name: 'edit_roster_template',
                          component: EditRosterTemplate,
                          meta: {
                            label: '$t__router_file.edit_roster_template',
                            routePermission: 'canEditServiceActivitiesTemplates',
                            iconClasses: 'las la-edit',
                          },
                        },
                        {
                          path: 'new',
                          name: 'create_roster_template',
                          component: CreateRosterTemplate,
                          meta: {
                            label: '$t__router_file.create_roster_template',
                            routePermission: 'canCreateServiceActivitiesTemplates',
                            iconClasses: 'las la-plus-circle',
                          },
                        },
                      ],
                    },
                    {
                      path: 'service-types',
                      meta: {
                        label: '$t__router_file.service_types',
                        routePermission: 'canListServiceTypes',
                      },
                      component: {
                        render(c) {
                          return c('router-view');
                        },
                      },
                      children: [
                        {
                          path: '',
                          name: 'all_service_types',
                          component: ServiceTypes,
                          meta: {
                            label: '$t__router_file.all_service_types',
                            iconClasses: 'las la-file-alt',
                            fullWidth: true,
                          },
                        },
                        {
                          path: ':id/edit',
                          name: 'edit_service_types',
                          component: ServiceTypeEditForm,
                          meta: {
                            label: '$t__router_file.edit_service_type',
                            routePermission: 'canEditServiceTypes',
                            iconClasses: 'las la-edit',
                            pageClasses: 'col-md-6 col-sm-9 col-xs-12',
                          },
                        },
                        {
                          path: 'new',
                          name: 'add_service_types',
                          component: ServiceTypeCreationForm,
                          meta: {
                            label: '$t__router_file.create_service_type',
                            routePermission: 'canCreateServiceTypes',
                            iconClasses: 'las la-plus-circle',
                            pageClasses: 'col-md-6 col-sm-9 col-xs-12',
                          },
                        },
                      ],
                    },
                    {
                      path: 'membership-manners',
                      meta: {
                        label: '$t__router_file.membership_manners',
                        routePermission: 'canViewOrganisationSettings',
                        iconClasses: 'las la-file-alt',
                      },
                      component: {
                        render(c) {
                          return c('router-view');
                        },
                      },
                      children: [
                        {
                          path: '',
                          name: 'all_membership_manners',
                          component: MembershipManners,
                          meta: {
                            label: '$t__router_file.all_membership_manners',
                            iconClasses: 'las la-file-alt',
                            fullWidth: true,
                          },
                        },
                        {
                          path: ':id/edit',
                          name: 'edit_membership_manners',
                          component: MembershipMannerEditForm,
                          meta: {
                            label: '$t__router_file.edit_membership_manners',
                            routePermission: 'canEditOrganisationSettings',
                            iconClasses: 'las la-edit',
                          },
                        },
                        {
                          path: 'new',
                          name: 'add_membership_manners',
                          component: MembershipMannerCreationForm,
                          meta: {
                            label: '$t__router_file.create_membership_manners',
                            routePermission: 'canEditOrganisationSettings',
                            iconClasses: 'las la-plus-circle',
                          },
                        },
                      ],
                    },
                    {
                      path: 'giving-types',
                      meta: {
                        label: '$t__router_file.giving_types',
                        routePermission: 'canViewOrganisationSettings',
                        iconClasses: 'las la-file-invoice-dollar',
                      },
                      component: {
                        render(c) {
                          return c('router-view');
                        },
                      },
                      children: [
                        {
                          path: '',
                          name: 'all_giving_types',
                          component: GivingTypes,
                          meta: {
                            label: '$t__router_file.all_giving_types',
                            iconClasses: 'las la-file-invoice-dollar',
                            fullWidth: true,
                          },
                        },
                        {
                          path: ':id/edit',
                          name: 'edit_giving_types',
                          component: GivingTypeEditForm,
                          meta: {
                            label: '$t__router_file.edit_giving_types',
                            routePermission: 'canEditOrganisationSettings',
                            iconClasses: 'las la-edit',
                          },
                        },
                        {
                          path: 'new',
                          name: 'add_giving_types',
                          component: GivingTypeCreationForm,
                          meta: {
                            label: '$t__router_file.create_giving_types',
                            routePermission: 'canEditOrganisationSettings',
                            iconClasses: 'las la-plus-circle',
                          },
                        },
                      ],
                    },
                    {
                      path: 'sms-templates',
                      meta: {
                        label: '$t__router_file.sms_templates',
                        routePermission: 'canViewOrganisationSettings',
                      },
                      component: {
                        render(c) {
                          return c('router-view');
                        },
                      },
                      children: [
                        {
                          path: '',
                          name: 'all_sms_templates',
                          component: SmsTemplates,
                          meta: {
                            label: '$t__router_file.all_sms_templates',
                            iconClasses: 'las la-file-alt',
                            fullWidth: true,
                          },
                        },
                        {
                          path: ':id/edit',
                          name: 'edit_sms_template',
                          component: SmsTemplateEditForm,
                          meta: {
                            label: '$t__router_file.edit_sms_template',
                            routePermission: 'canEditOrganisationSettings',
                            iconClasses: 'las la-edit',
                          },
                        },
                        {
                          path: 'new',
                          name: 'add_sms_template',
                          component: SmsTemplateCreationForm,
                          meta: {
                            label: '$t__router_file.create_sms_template',
                            routePermission: 'canEditOrganisationSettings',
                            iconClasses: 'las la-plus-circle',
                          },
                        },
                      ],
                    },
                  ],
                },

                {
                  path: 'onboarding-data-migration',
                  meta: {
                    label: '$t__router_file.onboarding_data_migration',
                    routePermission: () => isImpersonationActive() || isChurchOwner(),
                    noPageTopMargin: true,
                  },
                  component: {
                    render(c) {
                      return c('router-view');
                    },
                  },
                  children: [
                    {
                      path: '',
                      name: 'onboarding_data_migration',
                      component: OnboardingDataMigrationLanding,
                      meta: {
                        label: '$t__router_file.gotedo_onboarding_and_data_migration',
                        showTitleBar: true,
                        fullWidth: true,
                        noPageTopMargin: true,
                      },
                    },
                    {
                      path: 'stage-one',
                      redirect: 'stage-one/tasks/1',
                      component: {
                        render(c) {
                          return c('router-view');
                        },
                      },
                      children: [
                        {
                          path: '',
                          name: 'onboarding_data_migration_step_one',
                          component: OnboardingDataMigrationStageOne,
                          meta: {
                            label: '$t__router_file.gotedo_onboarding_and_data_migration',
                            subLabel:
                              '$t__router_file.onboarding_and_data_migration_stage_stage_one_sub_label',
                            showTitleBar: true,
                            fullWidth: true,
                            noPageTopMargin: true,
                            hideScrollToTop: true,
                          },
                        },
                        {
                          path: 'tasks/:tabIndex',
                          name: 'onboarding_data_migration_step_one_tabs',
                          component: OnboardingDataMigrationStageOne,
                          meta: {
                            label: '$t__router_file.gotedo_onboarding_and_data_migration',
                            subLabel:
                              '$t__router_file.onboarding_and_data_migration_stage_stage_one_sub_label',
                            showTitleBar: true,
                            fullWidth: true,
                            noPageTopMargin: true,
                            hideScrollToTop: true,
                          },
                        },
                      ],
                    },
                    {
                      path: 'stage-two',
                      redirect: 'stage-two/tasks/1',
                      meta: {
                        isOnboardingCompleted: true,
                        showTitleBar: false,
                      },
                      component: {
                        render(c) {
                          return c('router-view');
                        },
                      },
                      children: [
                        {
                          path: 'tasks/:tabIndex',
                          name: 'onboarding_data_migration_stage_two_tabs',
                          component: OnboardingDataMigrationStageTwo,
                          props: true,
                          meta: {
                            label: '$t__router_file.gotedo_onboarding_and_data_migration',
                            subLabel: '$t__router_file.stage_two_data_migration',
                            showTitleBar: true,
                            noPageTopMargin: true,
                            fullWidth: true,
                            hideScrollToTop: true,
                          },
                        },
                      ],
                    },
                  ],
                },

                {
                  path: 'logs',
                  meta: {
                    label: '$t__router_file.logs',
                    routePermission: 'canEditSelf',
                  },
                  component: {
                    render(c) {
                      return c('router-view');
                    },
                  },
                  children: [
                    {
                      path: 'login-records',
                      component: LoginRecords,
                      name: 'church_login_records',
                      meta: {
                        label: '$t__router_file.member_login_activities',
                        routePermission: 'canViewLogs',
                        iconClasses: 'las la-file-alt',
                        fullWidth: true,
                      },
                    },
                  ],
                },
              ],
            },

            {
              path: 'socials',
              meta: {
                label: '$t__router_file.socials',
                routePermission: 'canListMembersAccounts',
                iconClasses: 'las la-hashtag',
              },
              component: {
                render(c) {
                  return c('router-view');
                },
              },
              children: [
                {
                  path: 'followers',
                  component: ChurchFollowers,
                  name: 'church_followers',
                  meta: {
                    label: '$t__router_file.church_followers',
                    iconClasses: 'las la-hashtag',
                    fullWidth: true,
                  },
                },
                {
                  path: 'timeline',
                  component: ChurchTimeline,
                  name: 'church_timeline',
                  meta: {
                    label: '$t__router_file.church_timeline',
                    iconClasses: 'las la-paper-plane',
                    fullWidth: true,
                  },
                },
              ],
            },
          ],
        },

        {
          path: '/accounts',
          meta: {
            label: '$t__router_file.members_accounts',
            routePermission: () =>
              store.getters['permissions/canListUsers'] && store.getters['auth/isGlobalUser'],
          },
          component: {
            render(c) {
              return c('router-view');
            },
          },
          children: [
            {
              path: 'non-global-users',
              meta: {
                label: '$t__router_file.non_global_users',
                iconClasses: 'las la-users',
                routePermission: 'canListUsers',
              },
              component: {
                render(c) {
                  return c('router-view');
                },
              },
              children: [
                {
                  path: '',
                  component: Users,
                  name: 'non_global_users',
                  meta: {
                    label: '$t__router_file.all_non_global_users',
                    iconClasses: 'las la-users',
                    fullWidth: true,
                  },
                },
                {
                  path: ':userId/view',
                  component: User,
                  name: 'view_non_global_user',
                  meta: {
                    label: '$t__router_file.user_details',
                    routePermission: 'canViewUsers',
                    iconClasses: 'las la-user',
                  },
                  props: true,
                },
                {
                  path: ':userId/edit',
                  meta: {
                    label: '$t__router_file.edit_user_account',
                    routePermission: 'canEditUsers',
                    iconClasses: 'las la-user-edit',
                  },
                  name: 'edit_non_global_user',
                  component: EditNonGlobalUserForm,
                  props: true,
                },
              ],
            },
          ],
        },

        {
          path: 'roles',
          meta: {
            label: '$t__router_file.role_and_permissions',
            routePermission: 'canListRoles',
          },
          component: {
            render(c) {
              return c('router-view');
            },
          },
          children: [
            {
              path: '',
              component: RolesTable,
              name: 'all_roles',
              meta: {
                label: '$t__router_file.all_roles',
                iconClasses: 'las la-user-tag',
                fullWidth: true,
              },
            },
            {
              path: 'new',
              component: CreateRoleForm,
              name: 'create_role',
              meta: {
                label: '$t__router_file.create_new_role',
                routePermission: 'canEditRoles',
                iconClasses: 'las la-plus-circle',
              },
            },
            {
              path: ':id/view',
              meta: {
                label: '$t__router_file.view_role',
                routePermission: 'canViewRoles',
                iconClasses: 'las la-user-tag',
              },
              name: 'view_role',
              component: Role,
            },
            {
              path: ':id/edit',
              meta: {
                label: '$t__router_file.edit_role',
                routePermission: 'canEditRoles',
                iconClasses: 'las la-edit',
              },
              name: 'edit_role',
              component: EditRoleForm,
            },
            {
              path: ':id/users',
              meta: {
                label: '$t__router_file.view_role_users',
                routePermission: 'canViewRoles',
                iconClasses: 'las la-users',
                fullWidth: true,
              },
              name: 'role_users',
              component: ViewRoleUsers,
            },
            {
              path: ':id/permissions',
              meta: {
                label: '$t__router_file.view_role_permissions',
                routePermission: 'canViewPermissions',
                iconClasses: 'las la-user-check',
                fullWidth: true,
              },
              name: 'role_permissions',
              component: ViewRolePermissions,
            },
            {
              path: 'permissions',
              meta: {
                label: '$t__router_file.permissions',
                routePermission: 'canListPermissions',
              },
              component: {
                render(c) {
                  return c('router-view');
                },
              },
              children: [
                {
                  path: '',
                  component: PermissionsTable,
                  name: 'all_permissions',
                  meta: {
                    label: '$t__router_file.all_permissions',
                    iconClasses: 'las la-user-check',
                    fullWidth: true,
                  },
                },
                {
                  path: 'new',
                  component: CreatePermissionForm,
                  name: 'create_permission',
                  meta: {
                    label: '$t__router_file.create_new_permission',
                    routePermission: 'canEditPermissions',
                    iconClasses: 'las la-plus-circle',
                  },
                },
                {
                  path: ':id/view',
                  meta: {
                    label: '$t__router_file.view_permission',
                    routePermission: 'canViewPermissions',
                    iconClasses: 'las la-user-check',
                  },
                  name: 'view_permission',
                  component: Permission,
                },
                {
                  path: ':id/edit',
                  meta: {
                    label: '$t__router_file.edit_permission',
                    routePermission: 'canEditPermissions',
                    iconClasses: 'las la-edit',
                  },
                  name: 'edit_permission',
                  component: EditPermissionForm,
                },
                {
                  path: ':id/users',
                  meta: {
                    label: '$t__router_file.view_permission_users',
                    routePermission: 'canViewMembersAccounts',
                    iconClasses: 'las la-users',
                    fullWidth: true,
                  },
                  name: 'permission_users',
                  component: ViewPermissionUsers,
                },
                {
                  path: ':id/roles',
                  meta: {
                    label: '$t__router_file.view_permission_role',
                    routePermission: 'canViewRoles',
                    iconClasses: 'las la-user-tag',
                    fullWidth: true,
                  },
                  name: 'permission_roles',
                  component: ViewPermissionRoles,
                },
              ],
            },
          ],
        },

        {
          path: 'files',
          meta: {
            label: '$t__router_file.files',
            routePermission: 'canListDownloadableFiles',
          },
          component: {
            render(c) {
              return c('router-view');
            },
          },
          children: [
            {
              path: 'downloadable-files',
              meta: {
                label: '$t__router_file.downloadable_files',
                routePermission: 'canListDownloadableFiles',
              },
              component: {
                render(c) {
                  return c('router-view');
                },
              },
              children: [
                {
                  path: '',
                  name: 'all_downloadable_files',
                  component: DownloadableFiles,
                  meta: {
                    label: '$t__router_file.all_downloadable_files',
                    iconClasses: 'la la-file-download',
                    fullWidth: true,
                  },
                },
                {
                  path: 'new',
                  name: 'add_downloadable_file',
                  component: DownloadableFileCreateForm,
                  meta: {
                    label: '$t__router_file.new_downloadable_file',
                    routePermission: 'canCreateDownloadableFiles',
                    iconClasses: 'la la-file-medical',
                    pageClasses: 'col-xl-4 col-lg-6 col-12',
                  },
                },
                {
                  path: ':downloadableFileId/edit',
                  name: 'edit_downloadable_file',
                  component: DownloadableFileCreateForm,
                  meta: {
                    label: '$t__router_file.edit_downloadable_file',
                    routePermission: 'canEditDownloadableFiles',
                    iconClasses: 'la la-file-medical',
                    pageClasses: 'col-xl-4 col-lg-6 col-12',
                  },
                  props: true,
                },
              ],
            },
          ],
        },

        {
          path: 'global/churches',
          meta: {
            label: '$t__router_file.churches',
            routePermission: () =>
              isGlobalUser() &&
              store.getters['permissions/lazyPermissionResolver']('canListOrganisations'),
          },
          component: {
            render(c) {
              return c('router-view');
            },
          },
          children: [
            {
              path: '',
              name: 'all_organisations',
              component: Organisations,
              meta: {
                label: '$t__router_file.all_churches',
                iconClasses: 'las la-church',
                fullWidth: true,
              },
            },
            {
              path: 'new',
              name: 'add_organisation',
              //component: CreateOrganisationForm,
              meta: {
                label: '$t__router_file.add_new_church',
                routePermission: 'canCreateOrganisations',
                iconClasses: 'las la-plus-circle',
              },
            },
            {
              path: 'deleted',
              name: 'deleted_organisations',
              component: () => import('@/views/organisations/DeletedOrganisations.vue'),
              meta: {
                label: '$t__router_file.deleted_churches',
                routePermission: 'canDeleteOrganisations',
                fullWidth: true,
              },
            },
            {
              path: ':churchId/view/:tab?',
              name: 'view_organisation',
              component: Organisation,
              meta: {
                label: '$t__router_file.church_details',
                routePermission: 'canViewOrganisations',
                iconClasses: 'las la-church',
                showTitleBar: false,
                useDefaultPageTopPadding: false,
                fullWidth: true,
              },
            },
            {
              path: ':churchId/edit',
              name: 'edit_organisation',
              component: OrganisationEditForm,
              meta: {
                label: '$t__router_file.edit_church_details',
                routePermission: 'canEditOrganisations',
                iconClasses: 'las la-edit',
                pageClasses: 'col-xl-6 col-lg-8 col-sm-10 col-xs-12',
              },
            },
            {
              path: ':churchId/verification-documents',
              name: 'church_verification_docs',
              component: ChurchVerificationDocuments,
              meta: {
                label: '$t__router_file.church_verification_documents',
                routePermission: 'canViewOrganisations',
                iconClasses: 'las la-file-alt',
              },
            },
            {
              path: ':churchId/members-accounts/:userId/view',
              name: 'view_members_account_global',
              component: ChurchUserDetails,
              props: true,
              meta: {
                label: '$t__router_file.view_members_account',
                routePermission: 'canViewOrganisations',
                iconClasses: 'las la-users',
              },
            },
            {
              path: ':churchId/members-accounts/:userId/edit',
              name: 'edit_members_account_global',
              component: ChurchUserEditForm,
              props: true,
              meta: {
                label: '$t__router_file.edit_members_account',
                routePermission: 'canEditUsers',
                iconClasses: 'las la-user-edit',
              },
            },
            {
              path: ':churchId/members-accounts',
              name: 'church_members_accounts',
              component: ChurchMembersAccounts,
              meta: {
                label: '$t__router_file.church_members_accounts',
                routePermission: 'canViewOrganisations',
                iconClasses: 'las la-users',
                fullWidth: true,
              },
            },
            {
              path: ':churchId/members-profiles',
              name: 'church_members_profiles',
              component: ChurchMembersProfiles,
              meta: {
                label: '$t__router_file.church_members',
                routePermission: 'canViewOrganisations',
                iconClasses: 'las la-users',
                fullWidth: true,
              },
            },
            {
              path: ':churchId/services',
              name: 'church_services',
              component: ChurchServices,
              meta: {
                label: '$t__router_file.church_services',
                routePermission: 'canViewOrganisations',
                iconClasses: 'las la-church',
                fullWidth: true,
              },
            },
            {
              path: ':churchId/visitors',
              name: 'church_visitors',
              component: ChurchVisitors,
              meta: {
                label: '$t__router_file.church_visitors',
                routePermission: 'canViewOrganisations',
                iconClasses: 'las la-users',
                fullWidth: true,
              },
            },
            {
              path: ':churchId/zones',
              name: 'church_zones',
              component: ChurchZones,
              meta: {
                label: '$t__router_file.church_zones',
                routePermission: 'canViewOrganisations',
                iconClasses: 'las la-share-alt',
                fullWidth: true,
              },
            },
            {
              path: ':churchId/ministries',
              name: 'church_ministries',
              component: ChurchMinistries,
              meta: {
                label: '$t__router_file.church_ministries',
                routePermission: 'canViewOrganisations',
                iconClasses: 'las la-people-carry',
                fullWidth: true,
              },
            },
            {
              path: ':churchId/positions',
              name: 'church_positions',
              component: ChurchPositions,
              meta: {
                label: '$t__router_file.church_positions',
                routePermission: 'canViewOrganisations',
                iconClasses: 'las la-sitemap',
                fullWidth: true,
              },
            },
            {
              path: ':churchId/sms-center',
              name: 'church_sms_center',
              component: ChurchSmsCenter,
              meta: {
                label: '$t__router_file.church_sms_center',
                routePermission: 'canViewOrganisations',
                iconClasses: 'las la-tachometer-alt',
                fullWidth: true,
              },
            },
            {
              path: ':churchId/church-templates',
              name: 'church_templates',
              component: ChurchTemplatesLanding,
              meta: {
                label: '$t__router_file.church_sms_center',
                routePermission: 'canViewOrganisations',
                iconClasses: 'las la-tachometer-alt',
                fullWidth: true,
              },
            },
            {
              path: 'church-claims',
              meta: {
                label: '$t__router_file.church_claims',
                routePermission: 'canListChurchClaims',
              },
              component: {
                render(c) {
                  return c('router-view');
                },
              },
              children: [
                {
                  path: '',
                  name: 'church_claims',
                  component: ChurchClaims,
                  meta: {
                    label: '$t__router_file.church_claims',
                    // iconClasses: 'las la-tachometer-alt',
                    fullWidth: true,
                  },
                },
                {
                  path: 'church-claims/:claimId',
                  name: 'church_claim_details',
                  component: ChurchClaimsDetails,
                  meta: {
                    label: '$t__router_file.church_claim_details',
                    routePermission: 'canViewChurchClaims',
                    // iconClasses: 'las la-tachometer-alt',
                    fullWidth: true,
                  },
                },
              ],
            },
          ],
        },

        {
          path: 'logs',
          meta: {
            label: '$t__router_file.logs',
            routePermission: 'canEditSelf',
          },
          component: {
            render(c) {
              return c('router-view');
            },
          },
          children: [
            {
              path: 'login-records',
              component: LoginRecords,
              name: 'login_records',
              meta: {
                label: '$t__router_file.member_login_activities',
                routePermission: 'canViewLogs',
                iconClasses: 'las la-file-alt',
              },
            },
            {
              path: 'notifications',
              component: Notifications,
              name: 'notifications',
              meta: {
                label: '$t__router_file.my_notifications',
                routePermission: 'canEditSelf',
                iconClasses: 'las la-bell',
                fullWidth: true,
              },
            },
          ],
        },
        {
          path: 'settings',
          redirect: '/settings/account-settings/personal-profile',
          meta: {
            label: '$t__router_file.settings',
            routePermission: 'canEditSelf',
          },
          component: {
            render(c) {
              return c('router-view');
            },
          },
          children: [
            {
              path: 'global-settings',
              name: 'global_settings',
              component: GlobalSettings,
              meta: {
                label: '$t__router_file.global_settings',
                routePermission: 'canEditSystemSettings',
                iconClasses: 'las la-cogs',
                fullWidth: true,
              },
            },
            // Purely for redirection
            {
              path: 'account-settings',
              redirect: '/settings/account-settings/personal-profile',
            },
            {
              path: 'account-settings/:tab',
              name: 'view_account_settings',
              component: MyAccountSettings,
              meta: {
                label: '$t__router_file.my_personal_settings',
                routePermission: 'canEditSelf',
                iconClasses: 'las la-user-cog',
                noBackground: true,
                fullWidth: true,
              },
            },
          ],
        },

        {
          path: 'branch-management',
          meta: {
            label: '$t__router_file.branch_management',
            routePermission: 'canInteractWithBranchManagement',
          },
          component: {
            render(c) {
              return c('router-view');
            },
          },
          children: [
            {
              path: '',
              name: 'branch_management_dashboard',
              component: BranchManagementDashboard,
              meta: {
                label: '$t__router_file.branch_management_dashboard',
                iconClasses: 'las la-church',
                routePermission: 'canInteractWithBranchManagementDashboard',
              },
            },
            {
              path: 'pending-branch-management-requests',
              name: 'branch_management_requests',
              component: PendingBranchManagementRequests,
              meta: {
                label: '$t__router_file.pending_branch_management_requests',
                iconClasses: 'las la-church',
                routePermission: 'canInteractWithPendingBranchManagementRequests',
                fullWidth: true,
              },
            },
            {
              path: 'branch-levels',
              meta: {
                label: '$t__router_file.branch_levels',
                iconClasses: 'las la-church',
              },
              component: {
                render(c) {
                  return c('router-view');
                },
              },
              children: [
                {
                  path: '',
                  name: 'all_branch_levels',
                  component: BranchLevels,
                  meta: {
                    label: '$t__router_file.all_branch_levels',
                    iconClasses: 'la la-file-invoice-dollar',
                    fullWidth: true,
                  },
                },
              ],
            },
          ],
        },
        {
          path: 'denominations',
          meta: {
            label: '$t__router_file.denominations',
            routePermission: 'canListDenominations',
          },
          component: {
            render(c) {
              return c('router-view');
            },
          },
          children: [
            {
              path: '',
              name: 'all_denominations',
              component: DenominationRecords,
              meta: {
                label: '$t__router_file.all_denominations',
                iconClasses: 'las la-church',
                fullWidth: true,
              },
            },
            {
              path: ':slug/:denominationId',
              name: 'denomination',
              redirect: ':slug/:denominationId/view',
              props: true,
              component: {
                render(c) {
                  return c('router-view');
                },
              },
              meta: {
                label: '$t__router_file.denomination',
                iconClasses: 'las la-church',
                routePermission: 'canViewDenominations',
              },
              children: [
                {
                  path: 'view',
                  name: 'view_denomination',
                  props: true,
                  component: Denomination,
                  meta: {
                    label: '$t__router_file.denomination_details',
                    iconClasses: 'las la-church',
                    routePermission: 'canViewDenominations',
                    fullWidth: true,
                  },
                },
                {
                  path: 'churches',
                  name: 'view_denomination_churches',
                  props: true,
                  component: Organisations,
                  meta: {
                    label: '$t__router_file.denomination_churches',
                    iconClasses: 'las la-church',
                    routePermission: 'canListDenominationChurches',
                    fullWidth: true,
                  },
                },
                {
                  path: 'churches/:churchId/view',
                  name: 'view_denomination_church',
                  props: true,
                  component: Organisation,
                  meta: {
                    label: '$t__router_file.denomination_church_details',
                    iconClasses: 'las la-church',
                    routePermission: 'canViewDenominationChurches',
                    fullWidth: true,
                  },
                },
                {
                  path: 'levels/:denominationLevelId/branches',
                  name: 'view_denomination_level_branches',
                  props: true,
                  component: DenominationLevelBranches,
                  meta: {
                    label: '$t__router_file.denomination_level_branches',
                    iconClasses: 'las la-sitemap',
                    routePermission: 'canListDenominationLevelChurches',
                    fullWidth: true,
                  },
                },
                {
                  path: 'levels/:denominationLevelId/branches/:branchId/lower-branches',
                  name: 'view_denomination_level_branch_descendants',
                  props: true,
                  component: DenominationLevelBranches,
                  meta: {
                    label: '$t__router_file.denomination_level_branches',
                    iconClasses: 'las la-sitemap',
                    routePermission: 'canListDenominationLevelChurches',
                    fullWidth: true,
                  },
                },
                {
                  path: 'create-church',
                  name: 'create_denomination_church',
                  props: true,
                  component: CreateDenominationChurchForm,
                  meta: {
                    label: '$t__router_file.create_new_denomination_church',
                    iconClasses: 'las la-church',
                    routePermission: 'canCreateOrganisations',
                  },
                },
              ],
            },
          ],
        },

        {
          path: 'users',
          meta: {
            label: '$t__router_file.users',
            iconClasses: 'las la-users',
            routePermission: () => store.getters['auth/isGlobalUser'],
          },
          component: {
            render(c) {
              return c('router-view');
            },
          },
          children: [
            {
              path: '',
              // component: '',
              name: 'users',
              meta: {
                label: '$t__router_file.users',
                iconClasses: 'las la-users',
                fullWidth: true,
              },
              routePermission: () => store.getters['auth/isGlobalUser'],
            },
            {
              path: 'global-users',
              component: {
                render(c) {
                  return c('router-view');
                },
              },
              routePermission: 'canListGlobalUsers',
              children: [
                // List global users
                {
                  path: '',
                  component: () => import('@/views/global_users/GlobalUsers.vue'),
                  name: 'global_users',
                  meta: {
                    label: '$t__router_file.global_users',
                    iconClasses: 'las la-users',
                    fullWidth: true,
                  },
                  routePermission: 'canListGlobalUsers',
                },
                // view global user
                {
                  path: ':userId/view',
                  component: () => import('@/views/global_users/GlobalUserDetails.vue'),
                  name: 'view_global_user',
                  meta: {
                    label: '$t__router_file.global_user_details',
                    iconClasses: 'las la-user',
                    pageClasses: 'col-xl-6 col-lg-8 col-12',
                  },
                  routePermission: 'canViewGlobalUsers',
                },
              ],
            },

            {
              path: 'internal-account-requests',
              component: InternalAccountRequests,
              name: 'global_internal_account_requests',
              meta: {
                label: '$t__router_file.internal_account_requests',
                iconClasses: 'las la-users',
                fullWidth: true,
              },
              routePermission: () => store.getters['auth/isGlobalUser'],
            },
            {
              path: 'church-functions',
              component: () => import('@/views/church_functions/ChurchFunctions.vue'),
              name: 'church_functions',
              meta: {
                label: '$t__general.church_functions_module.church_functions',
                // iconClasses: 'las la-users',
                fullWidth: true,
              },
              routePermission: () => store.getters['auth/isGlobalUser'],
            },
            {
              path: 'sms-accounts',
              component: () => import('@/views/communications/sms/SmsAccounts.vue'),
              name: 'sms_accounts',
              meta: {
                label: '$t__router_file.sms_accounts',
                iconClasses: 'las la-comment-dollar',
                fullWidth: true,
              },
              routePermission: 'canListSmsAccounts',
            },
          ],
        },
        {
          path: 'product-subscriptions',
          meta: {
            routePermission: isGlobalUser,
          },
          component: () => import('@/views/subscriptions/ProductSubscriptionsLayout.vue'),
          children: [
            {
              path: '',
              component: () => import('@/views/subscriptions/ProductSubscriptions.vue'),
              name: 'product_subscriptions',
              meta: {
                label: '$t__general.subscription_module.product_subscriptions',
                fullWidth: true,
              },
              routePermission: 'canListProductSubscriptions',
            },
            {
              path: 'product-subscription-refunds',
              component: () => import('@/views/subscriptions/ProductSubscriptionRefunds.vue'),
              name: 'product_subscription_refunds',
              meta: {
                label: '$t__general.subscription_module.product_subscription_refunds',
                fullWidth: true,
              },
              routePermission: 'canListProductSubscriptionRefunds',
            },
            {
              path: 'product-subscription-cancellations',
              component: () => import('@/views/subscriptions/ProductSubscriptionCancellations.vue'),
              name: 'product_subscription_cancellations',
              meta: {
                label: '$t__general.subscription_module.product_subscription_cancellations',
                fullWidth: true,
              },
              routePermission: 'canListProductSubscriptions',
            },
            {
              path: 'subscription-enquiries',
              component: () => import('@/views/subscriptions/ProductSubscriptionEnquiries.vue'),
              name: 'subscription_enquiries',
              meta: {
                label: '$t__general.subscription_module.subscription_enquiries',
                fullWidth: true,
              },
              routePermission: 'canListProductSubscriptionEnquiries',
            },
          ],
        },
        {
          path: 'users/:userId',
          meta: {
            routePermission: isGlobalUser,
          },
          component: {
            render(c) {
              return c('router-view');
            },
          },
          children: [
            {
              path: 'mails/new',
              component: MailCompose,
              name: 'global_compose_mail',
              meta: {
                label: '$t__router_file.create_new_email',
                iconClasses: 'las la-envelope',
                pageClasses: 'col-xl-4 col-lg-6 col-12',
              },
            },
            {
              path: 'mails/dashboard',
              // component: RolesTable,
              name: 'global_mail_dashboard',
              meta: {
                label: '$t__router_file.user_mail_dashboard',
                iconClasses: 'las la-envelope',
                fullWidth: true,
              },
            },
          ],
        },
        {
          path: 'product-documentations',
          meta: {
            routePermission: isGlobalUser,
          },
          component: {
            render(c) {
              return c('router-view');
            },
          },
          children: [
            {
              path: '',
              component: ProductDocumentationsHome,
              name: 'product_documentations',
              meta: {
                label: '$t__router_file.product_documentations',
                // iconClasses: 'las la-pencil',
                fullWidth: true,
              },
            },
            {
              path: 'products/new',
              component: () => import('@/views/products_documentation/ProductCreationForm.vue'),
              name: 'create_docs_product',
              props: true,
              meta: {
                label: '$t__router_file.create_product',
                // iconClasses: 'las la-pencil',
                pageClasses: 'col-xs-12 col-sm-8 col-md-10 col-lg-6',
              },
            },
            {
              path: 'products/:productId/edit',
              component: () => import('@/views/products_documentation/ProductEditForm.vue'),
              name: 'edit_docs_product',
              props: true,
              meta: {
                label: '$t__router_file.edit_product',
                // iconClasses: 'las la-pencil',
                pageClasses: 'col-xs-12 col-sm-8 col-md-10 col-lg-6',
              },
            },
            {
              path: 'product/:productId/categories',
              component: () => import('@/views/products_documentation/DocsProductCategories.vue'),
              name: 'product_categories',
              props: true,
              meta: {
                label: '$t__router_file.categories',
                // iconClasses: 'las la-pencil',
                fullWidth: true,
              },
            },
            {
              path: 'product/:productId/categories/:categoryId/sub-categories',
              component: () => import('@/views/products_documentation/DocsProductCategories.vue'),
              name: 'category_subcategories',
              props: true,
              meta: {
                label: '$t__router_file.sub_categories',
                // iconClasses: 'las la-pencil',
                fullWidth: true,
              },
            },
            {
              path: 'product/:productId/categories/:categoryId/category-localisations',
              component: DocsCategoryLocalisations,
              name: 'category_localisations',
              props: true,
              meta: {
                label: '$t__router_file.category_localisations',
                // iconClasses: 'las la-pencil',
                fullWidth: true,
              },
            },
            {
              path: 'product/:productId/topics',
              component: DocsTopicsTable,
              name: 'product_topics',
              props: true,
              meta: {
                label: '$t__router_file.product_topics',
                // iconClasses: 'las la-pencil',
                fullWidth: true,
              },
            },
            {
              path: 'product/:productId/categories/:categoryId/topics',
              component: DocsTopicsTable,
              name: 'docs_category_topics',
              props: true,
              meta: {
                label: '$t__router_file.docs_category_topics',
                // iconClasses: 'las la-pencil',
                fullWidth: true,
              },
            },
            {
              path: 'product/:productId/soft-deleted-topics',
              component: DocsTopicsTable,
              name: 'soft_deleted_topics',
              props: true,
              meta: {
                label: '$t__router_file.soft_deleted_topics',
                // iconClasses: 'las la-pencil',
                fullWidth: true,
              },
            },
            {
              path: 'product/:productId/articles',
              component: DocsArticlesTable,
              name: 'docs_product_articles',
              props: true,
              meta: {
                label: '$t__router_file.docs_product_articles',
                // iconClasses: 'las la-pencil',
                fullWidth: true,
              },
            },
            {
              path: 'product/:productId/categories/:categoryId/articles',
              component: DocsArticlesTable,
              name: 'docs_category_articles',
              props: true,
              meta: {
                label: '$t__router_file.docs_category_articles',
                // iconClasses: 'las la-pencil',
                fullWidth: true,
              },
            },
            {
              path: 'product/:productId/categories/:categoryId/topics/:topicId/articles',
              component: DocsArticlesTable,
              name: 'docs_topic_articles',
              props: true,
              meta: {
                label: '$t__router_file.topic_articles',
                // iconClasses: 'las la-pencil',
                fullWidth: true,
              },
            },
            {
              path: 'checked-out-articles',
              component: DocsArticlesTable,
              name: 'checked_out_articles',
              props: true,
              meta: {
                label: '$t__router_file.checked_out_articles',
                // iconClasses: 'las la-pencil',
                fullWidth: true,
              },
            },
            {
              path: 'product/:productId/categories/:categoryId/articles/:articleId/edit',
              component: ArticleEditor,
              name: 'edit_article',
              props: true,
              meta: {
                label: '$t__router_file.edit_article',
                // iconClasses: 'las la-pencil',
                fullWidth: true,
              },
            },
            {
              path: 'product/:productId/categories/:categoryId/articles/:articleId/preview',
              component: () => import('@/views/products_documentation/ArticlePreview.vue'),
              name: 'preview_article',
              props: true,
              meta: {
                label: '$t__router_file.article_preview',
                // iconClasses: 'las la-pencil',
                fullWidth: true,
              },
            },
            {
              path: 'product/:productId/categories/:categoryId/articles/:articleId/versions',
              component: ArticleVersions,
              name: 'articles_versions',
              props: true,
              meta: {
                label: '$t__router_file.version_history',
                iconClasses: 'las la-pencil',
                fullWidth: true,
              },
            },
          ],
        },
        {
          path: 'legal-documents',
          meta: {
            routePermission: 'canListLegalDocuments',
          },
          component: {
            render(c) {
              return c('router-view');
            },
          },
          children: [
            {
              path: '',
              component: () => import('@/views/legal_documents/LegalDocuments.vue'),
              name: 'legal_documents',
              meta: {
                label: '$t__router_file.legal_documents',
                // iconClasses: 'las la-pencil',
                fullWidth: true,
              },
            },
            {
              path: ':legalDocumentId/localisations',
              component: () => import('@/views/legal_documents/LegalDocumentLocalisations.vue'),
              name: 'legal_document_localisations',
              props: true,
              meta: {
                label: '$t__router_file.legal_document_localisations',
                iconClasses: 'las la-pencil',
                fullWidth: true,
              },
            },
            {
              path: ':legalDocumentId/localisations/:legalDocumentLocalisationId/edit',
              component: () => import('@/views/legal_documents/LegalDocumentEditor.vue'),
              name: 'edit_legal_document_localisation',
              props: true,
              meta: {
                label: '$t__router_file.edit_legal_document_localisation',
                iconClasses: 'las la-pencil',
                fullWidth: true,
                routePermission: 'canEditLegalDocuments',
              },
            },
          ],
        },
        {
          path: 'moderation-reports',
          meta: {
            label: '$t__router_file.moderation_reports',
          },
          routePermission: 'canListModerationReports',
          component: {
            render(c) {
              return c('router-view');
            },
          },
          children: [
            {
              path: ':status?',
              component: () => import('@/views/moderation_reports/ModerationReports.vue'),
              name: 'moderation_reports',
              meta: {
                label: '$t__router_file.moderation_reports',
                routePermission: 'canListModerationReports',
                iconClasses: 'las la-file-alt',
                fullWidth: true,
              },
              props: true,
            },
            {
              path: ':entityTypeName/:entityId',
              component: () => import('@/views/moderation_reports/ReportedEntityDetail.vue'),
              name: 'moderation_reported_entity_details',
              props: true,
              meta: {
                label: '$t__router_file.reported_entity_details',
                routePermission: 'canViewModerationReports',
                fullWidth: true,
              },
            },
            {
              path: ':entityTypeName/:entityId/:reportId',
              component: () => import('@/views/moderation_reports/ModerationReport.vue'),
              name: 'moderation_report_details',
              props: true,
              meta: {
                label: '$t__router_file.moderation_report_details',
                routePermission: 'canViewModerationReports',
                fullWidth: true,
              },
            },
          ],
        },
      ],
    },

    {
      path: '/auth/login/church-selector',
      name: 'LoginChurchSelector',
      component: () => import('@/views/pages/LoginChurchSelector.vue'),
      meta: {
        guest: true,
        loginFlow: true,
        showSignInWithGoogle: true,
        showFormFooterLink: true,
      },
    },
    {
      path: '/auth/login',
      name: 'Login',
      component: () => import('@/views/pages/AuthLogin.vue'),
      meta: {
        guest: true,
        loginFlow: true,
        showSignInWithGoogle: true,
        showFormFooterLink: true,
      },
    },
    {
      path: '/auth/church-registration',
      name: 'ChurchRegistration',
      component: () => import('@/views/pages/ChurchRegistrationForm.vue'),
      meta: {
        guest: true,
      },
    },
    // Handle legacy church registration page (for SEO)
    {
      path: '/auth/register',
      name: 'ChurchRegistrationLegacy',
      meta: {
        guest: true,
      },
      redirect() {
        return {
          name: 'ChurchRegistration',
        };
      },
    },
    // Handle legacy password change page (for SEO)
    {
      path: '/auth/forgot_password',
      name: 'ForgotPasswordLegacy',
      meta: {
        guest: true,
      },
      beforeEnter() {
        window.location.replace('https://accounts.gotedo.com/en/auth/password-recovery');
      },
    },
    {
      path: '/auth/membership-self-registration/checkpoint/:orgId/:hash',
      name: 'membership_self_registration_landing',
      component: MembershipSelfRegistrationCheckpoint,
      props: true,
      meta: {
        guest: true,
        membershipSelfRegistrationFlow: true,
        isCheckpoint: true,
      },
    },
    {
      path: '/auth/membership-self-registration/registration-form/:orgId/:hash',
      name: 'membership_self_registration_form',
      component: MembershipSelfRegistrationForm,
      props: true,
      meta: {
        loggedInForSelfRegistration: true,
        guest: true,
        membershipSelfRegistrationFlow: true,
      },
    },
    {
      path: '/auth/membership-self-registration/success',
      name: 'MembershipSelfRegistrationSuccess',
      component: MembershipSelfRegistrationSuccess,
      meta: {
        guest: true,
        membershipSelfRegistrationFlow: true,
      },
    },
    {
      path: '/auth/account-activation/:userId/:activationCode/:churchId?',
      name: 'AccountActivation',
      component: AccountActivation,
      props: true,
      meta: {
        guest: true,
      },
    },
    {
      path: '/:catchAll(.*)',
      component: Page404,
      name: 'NotFound',
    },
  ],
  scrollBehavior(to) {
    if (to.hash) {
      return {
        selector: to.hash,
        behavior: 'smooth',
      };
    }
    return { x: 0, y: 0 };
  },
});

const debouncedLogoutUserDispatch = debounce(store.dispatch, 1000);

/**
 * Auto-redirect from login flow to dashboard if already logged in
 */
router.beforeEach(async (to, from, next) => {
  if (to.matched.some((record) => record.meta.guest && record.meta.loginFlow)) {
    if (!isLoggedIn()) {
      // Continue navigation
      return next();
    }
    // Redirect to `Home` route. Other redirects are defined on the `Home` route
    return next({ name: 'Home' });
  }

  next();
});

router.beforeEach(async (to, from, next) => {
  if (to.matched.some((record) => record.meta.requiresAuth)) {
    if (isLoggedIn()) {
      next();
    } else {
      await debouncedLogoutUserDispatch('auth/logoutUser', {
        message: i18next.t('router_file.you_are_not_logged_in', { ns: 'toasts' }),
      });
      store.commit('auth/setIntendedRoute', {
        name: to.name,
        params: to?.params ?? undefined,
      });
      next({ name: 'Login' });
    }
  } else next();
});

// Ensure that routes match the impersonated church
router.beforeEach((to, from, next) => {
  if (
    isImpersonationActive() &&
    to.params.churchId &&
    to.params.churchId !== impersonatedChurchId()
  ) {
    Notify.create({
      message: i18next.t('router_file.requested_route_does_not_match_impersonated_church', {
        ns: 'toasts',
      }),
      type: 'info',
    });
    return false;
  }
  return next();
});

/**
 * Check for grace period
 */
router.beforeEach(async (to, from, next) => {
  if (isImpersonationActive() || isGlobalUser()) {
    return next();
  }

  if (!isLoggedIn() || to.meta.guest) {
    return next();
  }

  if (isAuthOrgVerified()) {
    return next();
  } else {
    /**
     * Attempt to refetch auth profile
     *
     * CRITICAL: Ensures that states are initialised during full-page reload.
     * Without this, user might hit the `routeNoPermission` call below
     * even when their church is verified.
     */
    const isVerified = await loadAuthProfile(to.params.churchId || getAuthOrgId()).then(
      async () => {
        await Vue.nextTick();
        return isAuthOrgVerified();
      }
    );

    if (isVerified) {
      return next();
    }
  }

  if (
    [
      'subscription',
      'church_verification_docs_settings',
      'ChurchDashboard',
      'notifications',
      'my_church_profile',
      'my_profile_edit',
      'my_organisation',
      'Login',
      'view_account_settings',
      'church_details_settings',
    ].includes(to.name)
  ) {
    return next();
  }

  routeNoPermission(i18next.t('router_file.non_verification_access_denied', { ns: 'toasts' }));
  return next({ name: 'church_verification_docs_settings', params: to.params });
});

/**
 * Resolve all route permissions
 */
router.beforeEach(async (to, from, next) => {
  const matchedRoutes = to.matched;
  const isGuest = !!to.meta.guest;

  if (isGuest) {
    return next();
  }

  /**
   * Attempt to refetch route permissions
   *
   * CRITICAL: Ensures that states are initialised during full-page reload.
   * Without this, user might hit the `routeNoPermission` call below
   * even when they have route permissions.
   *
   * We are wrapping around the app readiness check, to ensure that
   * the route permissions call is only called during full-page reload.
   */
  if (!appReady.get()) {
    await loadRoutePermissions(isGlobalUser() ? undefined : to.params.churchId ?? churchId()).then(
      async () => {
        await Vue.nextTick();
      }
    );
  }

  const permitted = matchedRoutes.every((route) => {
    if (typeof route.meta.routePermission === 'undefined') {
      return true;
    }
    if (typeof route.meta.routePermission === 'boolean') {
      return route.meta.routePermission;
    }
    if (typeof route.meta.routePermission === 'function') {
      return route.meta.routePermission(to);
    }
    // String `routePermissions`
    const routePermissions = Array.isArray(route.meta.routePermission)
      ? route.meta.routePermission
      : typeof route.meta.routePermission === 'string'
      ? [route.meta.routePermission]
      : [];
    return routePermissions.some((perm) => {
      return store.getters['permissions/lazyPermissionResolver'](perm);
    });
  });

  if (!permitted) {
    /**
     * When switching from global context to church context,
     * permission maybe denied for the `continue` route after login.
     * Attempt to open the Dashboard via the `Home` route after showing the
     * warning.
     */
    routeNoPermission(
      i18next.t('router_file.access_to_page_denied', { ns: 'toasts', context: 'to_dashboard' })
    );
    return next({ name: 'Home' });
  }

  return next();
});

/**
 * Check for self-registration access
 */
router.beforeEach(async (to, from, next) => {
  const requiresMembershipSelfRegistrationLogin = to.matched.some(
    (record) => record.meta.loggedInForSelfRegistration
  );
  const isMembershipSelfRegistrationFlow = to.matched.some(
    (record) => record.meta.membershipSelfRegistrationFlow
  );
  const isMembershipSelfRegistrationCheckpoint = to.matched.some(
    (record) => record.meta.isCheckpoint
  );

  if (isMembershipSelfRegistrationFlow && isMembershipSelfRegistrationCheckpoint && isLoggedIn()) {
    // Forcefully logout the authenticated user
    await debouncedLogoutUserDispatch('auth/logoutUser', {
      message: i18next.t('router_file.logged_out_for_membership_self_registration', {
        ns: 'toasts',
      }),
      noPushToLoginPage: true,
      messageVariant: 'info',
    });

    store.commit('auth/setIntendedRoute', {
      name: from.name,
      params: from?.params ?? {},
    });

    return next();
  }

  if (requiresMembershipSelfRegistrationLogin) {
    const isTokenValid = await validateTokenPayload({
      hash: to.params.hash,
      churchId: to.params.orgId,
    });
    if (isTokenValid) {
      next();
    } else {
      routeNoPermission(
        i18next.t('router_file.self_registration_invalid_access', { ns: 'toasts' })
      );
      next({ name: 'membership_self_registration_landing', params: to.params });
    }
  } else next();
});

Vue.use(VueGtm, {
  id: process.env.NODE_ENV === 'production' && !inStagingMode ? 'GTM-M7W36ZV' : 'GTM-KSVJ75G',
  /* queryParams: {
    // Add url query string when load gtm.js with GTM ID (optional)
    gtm_auth: 'AB7cDEf3GHIjkl-MnOP8qr',
    gtm_preview: 'env-4',
    gtm_cookies_win: 'x',
  }, */
  defer: true,
  enabled: true,
  debug: process.env.NODE_ENV !== 'production',
  loadScript: true,
  // vueRouter: router, // Implemented in afterEach hook
  ignoredViews: [],
  trackOnNextTick: true,
});

/**
 * This solves the issue of having "undefined" in GTM history variables
 */
router.afterEach((to) => {
  const screenName = to.matched
    .map((route) => route.name || '')
    .filter((route) => !!route)
    .join('.');
  // Prefer tracking with route name not route meta label
  Vue.gtm.trackView(screenName, to.path, {
    uid: (() => store.getters['auth/getAuthUserID']())() || '',
    oid: (() => store.getters['auth/getAuthOrgID'])() || '',
  });
});

export default router;
